import React, { useState, useEffect, useLayoutEffect, useContext, useRef, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { 
    FaUsers, FaGhost, FaBolt, FaLock, FaPlus, FaChevronRight, FaChevronLeft, 
    FaTimes, FaSearch, FaFilter, FaCrown, FaHistory, FaHeart, FaLink, FaGem,
    FaGamepad, FaPlay, FaEdit, FaSyncAlt, FaRobot, FaTrash, FaQuestionCircle,
    FaWind, FaSkull, FaFastForward, FaDice, FaCoins, FaBriefcase, FaArrowUp, FaTrophy, 
    FaArrowDown, FaUser, FaBoxOpen, FaSortAmountDown, FaSortAmountUp, FaRandom, FaCheck, FaChevronDown, FaPiggyBank,
    FaCaretDown, FaCaretUp, FaCaretLeft, FaCaretRight, FaHandHoldingHeart, FaHandshake
} from 'react-icons/fa';
import { GiAncientSword, GiCrossedSwords } from 'react-icons/gi';
import { motion, AnimatePresence } from 'framer-motion';
import { UserContext } from './App';
import { ChatContext } from './ChatContext';
import { MOCK_CASES } from './Cases';

import './CaseBattles.css';

const BRAND_PURPLE = '#ffb703';

const ScrapIcon = ({ size = 14, color }) => (
    <img 
        src="/scrap.webp" 
        alt="scrap" 
        style={{ width: size, height: size, objectFit: 'contain', filter: color ? `drop-shadow(0 0 5px ${color})` : 'none' }} 
    />
);

// ========================================
// TOGGLE THIS TO ENABLE/DISABLE CASE BATTLES
// Set to false when ready to go live
// ========================================
const MAINTENANCE_MODE = false;

/* --- MOCK DATA --- */
const MOCK_AVATARS = [
    'https://api.dicebear.com/7.x/pixel-art/svg?seed=1',
    'https://api.dicebear.com/7.x/pixel-art/svg?seed=2',
    'https://api.dicebear.com/7.x/pixel-art/svg?seed=3',
    'https://api.dicebear.com/7.x/pixel-art/svg?seed=4',
];

const MOCK_BOTS = [
    { username: 'Heavy Scientist', avatar: '/607ndfxvqlf41.jpg' },
    { username: 'Scientist', avatar: '/oil-rig-scientist.png' },
    { username: 'Cobalt Scientist', avatar: '/nodefarmer.jpg' },
    { username: 'Node Farmer', avatar: '/oil-rig-scientist.png' },
];

const COL_PALETTE = [
    { c: '#ffb703', g: 'rgba(124, 58, 237, 0.1)' }, // Rust Orange
    { c: '#3b82f6', g: 'rgba(59, 130, 246, 0.1)' }, // Blue
    { c: '#22c55e', g: 'rgba(34, 197, 94, 0.1)' }, // Green
    { c: '#ef4444', g: 'rgba(239, 68, 68, 0.1)' }, // Red
];

const getCaseRisk = (caseObj) => {
    if (!caseObj) return 0;
    if (caseObj.manualRisk !== undefined) return caseObj.manualRisk;
    if (!caseObj.items) return 0;
    const totalChance = caseObj.items.reduce((acc, item) => acc + (item.chance || 0), 0);
    const winChance = caseObj.items
        .filter(i => i.price >= caseObj.price)
        .reduce((acc, item) => acc + ((item.chance || 0) / totalChance), 0);
    return Math.max(5, (1 - winChance) * 100);
};

const getWeightedItem = (items) => {
    const totalChance = items.reduce((acc, item) => acc + (item.chance || 0), 0);
    const randomRoll = Math.random() * totalChance;
    let cumulative = 0;
    for (const item of items) {
        cumulative += (item.chance || 0);
        if (randomRoll <= cumulative) {
            return item;
        }
    }
    return items[items.length - 1];
};

// Enrich battle data with correct images from MOCK_CASES (server may send stale paths)
const enrichBattle = (battle) => {
    if (!battle) return battle;
    const enrichedCases = (battle.cases || []).map(c => {
        const full = MOCK_CASES.find(mc => mc.id === c.id || mc.name === c.name);
        if (full) return { ...c, img: full.img, items: full.items };
        return c;
    });
    return { ...battle, cases: enrichedCases };
};
const enrichBattles = (list) => (list || []).map(enrichBattle);

const PrefRow = ({ name, icon, active }) => (
    <div className={`pref-card ${active ? 'active' : ''}`}>
        <div className="p-icon">{icon}</div>
        <div className="p-name">{name}</div>
    </div>
);

const CaseBattles = () => {
    const { user } = useContext(UserContext);
    const { socket } = useContext(ChatContext);
    const [view, setView] = useState('list'); 
    const [selectedBattleId, setSelectedBattleId] = useState(null);
    const [inspectedCaseRaw, setInspectedCaseRaw] = useState(null);
    const [showCaseSelector, setShowCaseSelector] = useState(false); // LIFTED FOR CENTERING
    const inspectedCase = useMemo(() => {
        if (!inspectedCaseRaw) return null;
        if (inspectedCaseRaw.items) return inspectedCaseRaw;
        
        // Enrich from MOCK_CASES if items are missing (common when inspecting from backend-provided battles)
        const fullCase = MOCK_CASES.find(c => c.id === inspectedCaseRaw.id || c.name === inspectedCaseRaw.name);
        if (fullCase) {
            return { ...inspectedCaseRaw, items: fullCase.items, manualRisk: fullCase.manualRisk };
        }
        return inspectedCaseRaw;
    }, [inspectedCaseRaw]);

    const setInspectedCase = (c) => setInspectedCaseRaw(c);

    // ADMIN LOCK LOGIC
    const isAdmin = user?.isAdmin || user?.role === 'admin' || user?.username === 'Admin';
    
    const [battles, setBattles] = useState([]);

    useEffect(() => {
        if (socket) {
            socket.emit('join_case_battles_lobby');
            socket.emit('get_case_battle_history');
            socket.on('case_battles_update', (data) => {
                const enriched = enrichBattles(data);
                setBattles(prev => {
                    // Merge: keep any private battles we already have loaded via direct link
                    const publicIds = new Set(enriched.map(b => b.id));
                    const privateKept = prev.filter(b => b.isPrivate && !publicIds.has(b.id));
                    return [...privateKept, ...enriched];
                });
            });
            socket.on('case_battle_data', (rawBattle) => {
                const battle = enrichBattle(rawBattle);
                // Direct-link access — inject this battle into our local state
                setBattles(prev => {
                    const exists = prev.find(b => b.id === battle.id);
                    if (exists) return prev.map(b => b.id === battle.id ? battle : b);
                    return [battle, ...prev];
                });

                // Auto-navigate to the battle if the current hash matches this battle ID
                if (window.location.hash.includes(battle.id)) {
                    setSelectedBattleId(battle.id);
                    if (battle.status === 'completed') setView('results');
                    else setView('opening');
                }
            });
            socket.on('case_battle_created', (rawBattle) => {
                const newBattle = enrichBattle(rawBattle);
                setBattles(prev => {
                    const exists = prev.find(b => b.id === newBattle.id);
                    if (exists) return prev.map(b => b.id === newBattle.id ? newBattle : b);
                    return [newBattle, ...prev];
                });
                setSelectedBattleId(newBattle.id);
                setView('opening');
            });
            socket.on('case_battle_joined', (battle) => {
                setSelectedBattleId(battle.id);
                setView('opening');
            });
            socket.on('case_battle_history', (historyData) => {
                const enriched = enrichBattles(historyData);
                setBattles(prev => {
                    // Filter out any existing completed battles to avoid duplicates
                    const activeOnly = prev.filter(b => b.status !== 'completed');
                    return [...activeOnly, ...enriched];
                });
            });
            socket.on('case_battle_error', (msg) => {
                if (window._addToast) window._addToast({ type: 'error', title: 'BATTLE ERROR', message: msg });
            });

            return () => {
                socket.off('case_battles_update');
                socket.off('case_battle_data');
                socket.off('case_battle_created');
                socket.off('case_battle_joined');
                socket.off('case_battle_error');
            };
        }
    }, [socket]);

    const handleCreateBattle = (config) => {
        if (!user) {
            if (window._addToast) window._addToast({ type: 'error', title: 'AUTH ERROR', message: 'You must be logged in to create a battle' });
            return;
        }
        
        const baseCost = (config.caseIds || []).reduce((acc, cid) => {
            const c = MOCK_CASES.find(it => it.id === cid);
            return acc + (c?.price || 0);
        }, 0);

        const fundingPercent = config.fundingPercent || 0;
        const totalCost = baseCost + (baseCost * ((config.playerCount || 2) - 1) * (fundingPercent / 100));

        if ((user.balance || 0) < totalCost) {
            if (window._addToast) window._addToast({ type: 'error', title: 'BALANCE ERROR', message: `Insufficient balance! You need ${totalCost.toFixed(2)} scrap to create this funded battle.` });
            return;
        }

        if (socket) {
            socket.emit('create_case_battle', config);
        }
    };

    const handleViewBattle = (id, join = false) => {
        const b = battles.find(it => it.id === id);
        if (!b) return;
        setSelectedBattleId(id);
        if (b.status === 'completed') setView('results');
        else {
            if (socket) {
                if (join) socket.emit('join_case_battle', { 
                    battleId: id
                });
                else socket.emit('view_case_battle', { battleId: id });
            }
            setView('opening');
        }
    };

    const handleRecreateBattle = () => {
        const b = battles.find(it => it.id === selectedBattleId);
        if (!b) return;
        
        handleCreateBattle({
            caseIds: (b.cases || []).map(c => c.id),
            mode: b.mode,
            playerCount: b.playerCount,
            isPrivate: b.isPrivate,
            isJackpot: b.isJackpot || (b.prefs && b.prefs.includes('Jackpot')),
            isCrazy: b.isCrazy || (b.prefs && b.prefs.includes('Crazy')),
            prefs: b.prefs || [],
            usePromoBalance: false
        });
    };

    const [editData, setEditData] = useState(null);

    const handleEditBattle = () => {
        const b = battles.find(it => it.id === selectedBattleId);
        if (!b) return;
        setEditData({
            mode: b.mode,
            cases: b.cases,
            prefs: b.prefs || [],
            playerCount: b.playerCount
        });
        setSelectedBattleId(null);
        setView('create');
        window.location.hash = ''; // Clear hash when editing
    };

    const updateBattle = (id, data) => {
        setBattles(prev => prev.map(b => b.id === id ? { ...b, ...data } : b));
    };

    // --- DIRECT LINKING LOGIC ---
    useEffect(() => {
        const handleHash = () => {
            const hash = window.location.hash;
            if (hash.startsWith('#/battle/')) {
                const id = hash.replace('#/battle/', '');
                // Use a functional state update to check the latest battles array
                setBattles(currentBattles => {
                    const b = currentBattles.find(it => it.id === id);
                    if (b) {
                        setSelectedBattleId(id);
                        if (b.status === 'completed') setView('results');
                        else setView('opening');
                    } else {
                        // Not found locally (guest or direct link to archived battle)
                        // Request data from server
                        if (socket) {
                            socket.emit('view_case_battle', { battleId: id });
                            setSelectedBattleId(id);
                        }
                    }
                    return currentBattles;
                });
            }
        };

        // Only handle hash on mount and when it actually changes
        window.addEventListener('hashchange', handleHash);
        handleHash();
        
        return () => window.removeEventListener('hashchange', handleHash);
    }, [socket]); // Re-run when socket is ready

    useEffect(() => {
        // Prevent clearing the hash on initial mount if we are trying to load a battle
        const isBattleHash = window.location.hash.startsWith('#/battle/');
        
        if (selectedBattleId && (view === 'opening' || view === 'results')) {
            window.location.hash = `/battle/${selectedBattleId}`;
        } else if (view === 'list' && !isBattleHash) {
            window.location.hash = '';
        }
    }, [selectedBattleId, view]);




    // --- FULL CASE BATTLES UI ---
    return (
        <div className="case-battles-main">
            <AnimatePresence mode="wait">
                {view === 'list' && (
                    <motion.div 
                        key="list" 
                        initial={{ opacity: 0, y: 15 }} 
                        animate={{ opacity: 1, y: 0 }} 
                        exit={{ opacity: 0, y: -15 }}
                        transition={{ duration: 0.35, ease: [0.23, 1, 0.32, 1] }}
                    >
                        <ListView onCreateClick={() => setView('create')} battles={battles} onView={handleViewBattle} onInspect={setInspectedCase} user={user} socket={socket} />
                    </motion.div>
                )}
                {view === 'create' && (
                    <motion.div 
                        key="create" 
                        initial={{ opacity: 0, y: 15 }} 
                        animate={{ opacity: 1, y: 0 }} 
                        exit={{ opacity: 0, y: -15 }}
                        transition={{ duration: 0.35, ease: [0.23, 1, 0.32, 1] }}
                    >
                        <CreateView onBack={() => { setView('list'); setEditData(null); }} 
                            onCreate={handleCreateBattle} onInspect={setInspectedCase} editData={editData} user={user} 
                            openCaseSelector={setShowCaseSelector} showCaseSelector={showCaseSelector} />
                    </motion.div>
                )}
                {view === 'opening' && (
                    <motion.div 
                        key="opening" 
                        initial={{ opacity: 0, y: 15 }} 
                        animate={{ opacity: 1, y: 0 }} 
                        exit={{ opacity: 0, y: -15 }}
                        transition={{ duration: 0.35, ease: [0.23, 1, 0.32, 1] }}
                    >
                        <BattleOpeningView 
                            battleId={selectedBattleId} 
                            battles={battles} 
                            updateBattle={updateBattle}
                            onBack={() => setView('list')}
                            onComplete={() => setView('results')}
                            onInspect={setInspectedCase}

                        />
                    </motion.div>
                )}
                {view === 'results' && (
                    <motion.div 
                        key="results" 
                        initial={{ opacity: 0, y: 15 }} 
                        animate={{ opacity: 1, y: 0 }} 
                        exit={{ opacity: 0, y: -15 }}
                        transition={{ duration: 0.35, ease: [0.23, 1, 0.32, 1] }}
                    >
                        <BattleResultsView 
                            battleId={selectedBattleId} 
                            battles={battles} 
                            onBack={() => setView('list')}
                            onInspect={setInspectedCase}

                            onRecreate={handleRecreateBattle}
                            onEdit={handleEditBattle}
                            user={user}
                        />
                    </motion.div>
                )}
            </AnimatePresence>



            {/* SHARED INSPECTION MODAL */}
            <AnimatePresence>
                {inspectedCase && (
                    <div className="modal-overlay" onClick={() => setInspectedCase(null)}>
                        <motion.div 
                            initial={{ scale: 0.9, opacity: 0 }} 
                            animate={{ scale: 1, opacity: 1 }} 
                            exit={{ scale: 0.9, opacity: 0 }} 
                            className="inspect-modal-abyss" 
                            onClick={e => e.stopPropagation()}
                        >
                            <FaTimes className="close-btn-abyss" onClick={() => setInspectedCase(null)} />
                            
                            <div className="inspect-header-abyss">
                                <div className="case-img-wrap">
                                    <img src={inspectedCase.img} alt="" />
                                </div>
                                <div className="inspect-info-abyss">
                                    <div className="name-row">
                                        <h2>{inspectedCase.name}</h2>
                                        <div className="originals-tag">Originals</div>
                                    </div>
                                    <div className="inspect-price-abyss">
                                        <img src="/scrap.webp" style={{width: '20px'}} alt=""/>
                                        <div className="val">{inspectedCase.price.toFixed(2)}</div>
                                    </div>
                                    <div className="inspect-stats-abyss">
                                        <div className="inspect-stat-item">Opened: <b>0</b></div>
                                        <div className="inspect-stat-item">Best Win: <img src="/scrap.webp" style={{width: '12px', marginLeft: '5px'}} alt=""/> <b>0.00</b></div>
                                    </div>
                                </div>
                            <div className="risk-slider-abyss">
                                <div className="lbl">{getCaseRisk(inspectedCase) > 75 ? 'BIG RISK' : (getCaseRisk(inspectedCase) > 40 ? 'MEDIUM RISK' : 'LOW RISK')}</div>
                                <div className="risk-track-abyss">
                                    <div className="risk-fill-abyss" style={{ width: `${getCaseRisk(inspectedCase)}%` }} />
                                    <div className="risk-thumb-abyss" style={{ left: `${getCaseRisk(inspectedCase)}%` }} />
                                </div>
                            </div>
                            </div>

                            <div className="inspect-grid-abyss">
                                {inspectedCase.items?.map((item, i) => (
                                    <div key={i} className="item-card-abyss">
                                        <div className="chance-badge">{item.chance}%</div>
                                        <div className="flame-wrap">
                                            <div className="flame-aura" style={{ background: `radial-gradient(circle, ${item.color} 0%, transparent 70%)` }} />
                                            <img src={item.img} alt="" />
                                        </div>
                                        <div className="item-name" style={{ color: item.color }}>{item.name}</div>
                                        <div className="item-price">
                                            <img src="/scrap.webp" style={{width: '14px'}} alt=""/>
                                            {item.price.toFixed(2)}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </motion.div>
                    </div>
                )}
            </AnimatePresence>
        </div>
    );
};

/* --- LIST VIEW --- */
const PREF_BADGE_META = {
    'Fast Mode': { color: '#ffb703', icon: <FaBolt/>, title: 'Fast Mode' },
    'Crazy':     { color: '#ffb703', icon: <FaSyncAlt/>, title: 'Crazy (Lowest Wins)' },
    'Jackpot':   { color: '#ffb703', icon: <FaCoins/>, title: 'Jackpot' },

    'Private':   { color: '#64748b', icon: <FaLock/>, title: 'Private' },
};

const ListView = ({ onCreateClick, battles, onView, onInspect, user, socket }) => {
    const [sortMode, setSortMode] = useState('newest');
    const [minPrice, setMinPrice] = useState('');
    const [maxPrice, setMaxPrice] = useState('');
    const [searchQuery, setSearchQuery] = useState('');
    const [showSort, setShowSort] = useState(false);
    const [activeTab, setActiveTab] = useState('active'); // active, history, mine

    useEffect(() => {
        if (socket && (activeTab === 'history' || activeTab === 'mine')) {
            socket.emit('get_case_battle_history');
        }
    }, [socket, activeTab]);

    const filteredBattles = useMemo(() => {
        let result = battles;
        
        // Tab Filtering
        if (activeTab === 'active') {
            // Public active battles only
            result = result.filter(b => (b.status === 'waiting' || b.status === 'in_progress') && !b.isPrivate);
        } else if (activeTab === 'history') {
            // Public completed battles only
            result = result.filter(b => b.status === 'completed' && !b.isPrivate);
        } else if (activeTab === 'mine') {
            // Show all my battles (public and private)
            result = result.filter(b => b.slots.some(s => s?.id === user?.id || s?.username === user?.username));
        }

        // Searching
        if (searchQuery) {
            const q = searchQuery.toLowerCase();
            result = result.filter(b => 
                b.mode.toLowerCase().includes(q) || 
                b.cases.some(c => c.name.toLowerCase().includes(q))
            );
        }

        // Filtering
        if (minPrice) result = result.filter(b => b.cost >= parseFloat(minPrice));
        if (maxPrice) result = result.filter(b => b.cost <= parseFloat(maxPrice));

        // Sorting
        if (sortMode === 'high') result.sort((a, b) => b.cost - a.cost);
        else if (sortMode === 'low') result.sort((a, b) => a.cost - b.cost);
        else if (sortMode === 'newest') result.sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0));
        else if (sortMode === 'oldest') result.sort((a, b) => (a.createdAt || 0) - (b.createdAt || 0));

        return result;
    }, [battles, activeTab, sortMode, minPrice, maxPrice, searchQuery]);

    const totalValue = battles.reduce((a, b) => a + (b.cost || 0), 0);
    const activeCount = battles.filter(b => b.status === 'active').length;
    const joinableCount = battles.filter(b => b.status === 'active' && b.slots?.some(s => !s)).length;

    const renderBattleRow = (b) => {
        const isTeam = b.mode === 'team';
        const is1v1 = b.mode === 'normal' && b.slots.length === 2;
        const half = Math.ceil(b.slots.length / 2);
        
        // For 1v1 or Team mode, split the slots for a VS layout
        const leftSlots = (isTeam || is1v1) ? b.slots.slice(0, half) : b.slots;
        const rightSlots = (isTeam || is1v1) ? b.slots.slice(half) : [];
        
        const maxCases = 4;
        const visibleCases = b.cases.slice(0, maxCases);
        const extraCases = b.cases.length - maxCases;
        const totalSlots = b.slots.length;
        const modeLabel = isTeam ? `${totalSlots/2}V${totalSlots/2}`
            : totalSlots === 2 ? '1V1'
            : totalSlots === 3 ? '1V1V1'
            : totalSlots === 4 ? '1V1V1V1'
            : `${totalSlots} PLAYER`;
        const modeColor = (b.isCrazy || b.isJackpot) ? '#ffb703' : (isTeam ? '#ffb703' : '#ffb703');
        const isCompleted = b.status === 'completed';
        const activePlayerCount = b.slots.filter(s => s).length;
        const joinable = b.slots.some(s => !s);

        const renderRowAvatar = (s, i) => {
            if (!s) return (
                <div key={`empty-${i}`} style={{ width: '32px', height: '32px', borderRadius: '8px', background: 'rgba(255,255,255,0.05)', border: '1px dashed rgba(255,255,255,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'rgba(255,255,255,0.2)' }}>
                    <FaUser size={12} />
                </div>
            );
            const isWinner = b.winners?.includes(s.id);
            return (
                <div key={s.id} style={{ position: 'relative', width: '32px', height: '32px' }}>
                    {isWinner && <FaCrown style={{ position: 'absolute', top: '-10px', left: '50%', transform: 'translateX(-50%)', color: '#ffb703', fontSize: '12px', zIndex: 10, filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.5))' }} />}
                    <img src={s.avatar || `https://api.dicebear.com/7.x/pixel-art/svg?seed=${s.username || s.id}`} alt="" style={{ width: '100%', height: '100%', borderRadius: '8px', objectFit: 'cover', border: isWinner ? '2px solid #ffb703' : '1px solid rgba(255,255,255,0.05)', background: '#1c1c26' }} />
                </div>
            );
        };

        const handleRowClick = () => {
            // Always just view the battle - user can choose a slot inside
            onView(b.id, false);
        };

        return (
            <div 
                key={b.id} 
                onClick={handleRowClick}
                style={{ 
                    background: '#121217', 
                    border: '1px solid rgba(255,255,255,0.03)', 
                    borderRadius: '12px', 
                    padding: '15px 25px', 
                    marginBottom: '10px', 
                    display: 'flex', 
                    alignItems: 'center', 
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                    position: 'relative',
                    overflow: 'hidden'
                }}
                onMouseEnter={e => { e.currentTarget.style.background = '#181820'; e.currentTarget.style.borderColor = 'rgba(255,255,255,0.08)'; }}
                onMouseLeave={e => { e.currentTarget.style.background = '#121217'; e.currentTarget.style.borderColor = 'rgba(255,255,255,0.03)'; }}
            >
                {/* Left Side: Mode and Players */}
                <div style={{ width: '220px', display: 'flex', flexDirection: 'column', gap: '8px', flexShrink: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <span style={{ fontSize: '11px', fontWeight: '900', color: modeColor, textTransform: 'uppercase', display: 'flex', alignItems: 'center', gap: '6px', letterSpacing: '0.5px' }}>
                            {b.isJackpot ? <FaCoins /> : isTeam ? <FaUsers /> : <GiCrossedSwords />}
                            {b.isJackpot ? 'JACKPOT' : isTeam ? 'GROUP' : 'NORMAL'}
                        </span>
                        {b.prefs && b.prefs.map(p => {
                            const meta = PREF_BADGE_META[p];
                            if (!meta || p === 'Jackpot') return null; // Already handled jackpot
                            return (
                                <div key={p} style={{ background: `${meta.color}22`, color: meta.color, padding: '2px 6px', borderRadius: '4px', fontSize: '9px', fontWeight: '900', textTransform: 'uppercase' }}>
                                    {meta.title}
                                </div>
                            );
                        })}
                    </div>
                    
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                        {leftSlots.map((s, i) => (
                            <div key={`l-${i}`} style={{ position: 'relative' }}>
                                {renderRowAvatar(s, i)}
                            </div>
                        ))}
                        {(isTeam || is1v1) && <div style={{ color: 'rgba(255,255,255,0.2)', fontSize: '12px', fontWeight: '900', margin: '0 2px' }}>{isTeam ? '-' : 'x'}</div>}
                        {rightSlots.map((s, i) => (
                            <div key={`r-${i}`} style={{ position: 'relative' }}>
                                {renderRowAvatar(s, i + 100)}
                            </div>
                        ))}
                    </div>
                </div>

                {/* Middle Side: Cases */}
                <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: '8px', padding: '0 20px', borderLeft: '1px solid rgba(255,255,255,0.05)', borderRight: '1px solid rgba(255,255,255,0.05)', overflow: 'hidden' }}>
                    {visibleCases.map((c, i) => (
                        <div key={i} style={{ width: '45px', height: '45px', background: 'rgba(255,255,255,0.02)', borderRadius: '8px', border: '1px solid rgba(255,255,255,0.04)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}
                            onContextMenu={(e) => {
                                if (onInspect) { e.preventDefault(); e.stopPropagation(); onInspect(c); }
                            }}>
                            <img src={c.img} alt={c.name} style={{ width: '30px', height: '30px', objectFit: 'contain', filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.5))' }} />
                        </div>
                    ))}
                    {extraCases > 0 && (
                        <div style={{ width: '45px', height: '45px', background: 'rgba(255,255,255,0.05)', borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'rgba(255,255,255,0.5)', fontSize: '12px', fontWeight: '900', flexShrink: 0 }}>
                            +{extraCases}
                        </div>
                    )}
                </div>

                {/* Right Side: Cost and Actions */}
                <div style={{ width: '280px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', paddingLeft: '25px', flexShrink: 0 }}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                        <div style={{ fontSize: '10px', fontWeight: '900', color: 'rgba(255,255,255,0.3)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                            {(!isCompleted && b.creatorId === user?.id) ? 'TOTAL COST' : (b.fundingPercent > 0 && !isCompleted ? `${b.fundingPercent}% FUNDED` : 'BATTLE COST')}
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', background: 'rgba(0,0,0,0.2)', padding: '6px 12px', borderRadius: '8px', border: '1px solid rgba(255,255,255,0.03)' }}>
                            <ScrapIcon size={12} color="#ffb703" />
                            <span style={{ fontSize: '14px', fontWeight: '900', color: 'white' }}>
                                {(!isCompleted && b.creatorId === user?.id) ? 
                                    (b.cost + (b.cost * (b.slots.length - 1) * (b.fundingPercent / 100))).toFixed(2) 
                                    : (isCompleted ? b.cost.toFixed(2) : (b.fundingPercent === 100 ? '0.00' : (b.cost * (1 - (b.fundingPercent / 100))).toFixed(2)))
                                }
                            </span>
                        </div>
                    </div>
                    
                    <button 
                        style={{ 
                            background: joinable ? 'rgba(34, 197, 94, 0.1)' : 'rgba(255,255,255,0.03)', 
                            border: joinable ? '1px solid rgba(34, 197, 94, 0.2)' : '1px solid rgba(255,255,255,0.05)', 
                            color: joinable ? '#22c55e' : 'white', 
                            padding: '10px 20px', 
                            borderRadius: '8px', 
                            fontSize: '11px', 
                            fontWeight: '900', 
                            textTransform: 'uppercase', 
                            cursor: 'pointer',
                            transition: 'all 0.2s',
                            boxShadow: joinable ? '0 0 15px rgba(34, 197, 94, 0.1)' : 'none'
                        }}
                        onMouseEnter={e => {
                            if (joinable) {
                                e.currentTarget.style.background = 'rgba(34, 197, 94, 0.2)';
                            } else {
                                e.currentTarget.style.background = 'rgba(255,255,255,0.08)';
                            }
                        }}
                        onMouseLeave={e => {
                            if (joinable) {
                                e.currentTarget.style.background = 'rgba(34, 197, 94, 0.1)';
                            } else {
                                e.currentTarget.style.background = 'rgba(255,255,255,0.03)';
                            }
                        }}
                        onClick={(e) => { e.stopPropagation(); onView(b.id, false); }}
                    >
                        {isCompleted ? 'VIEW RESULTS' : joinable ? 'JOIN BATTLE' : 'WATCH'}
                    </button>
                </div>
            </div>
        );
    };

    const SORT_LABELS = {
        high: 'PRICE: HIGH TO LOW',
        low: 'PRICE: LOW TO HIGH',
        newest: 'NEWEST BATTLES',
        oldest: 'OLDEST BATTLES'
    };

    return (
        <div className="cb-listview-wrap">
            {/* CASES STYLE HEADER */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '30px', maxWidth: '1200px', width: '100%', margin: '0 auto 30px auto' }}>
                <div style={{ display: 'flex', flexDirection: 'column' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', color: 'white', fontSize: '20px', fontWeight: '900', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                        <GiCrossedSwords style={{ color: '#ffb703' }} size={24} /> CASE BATTLES
                    </div>
                    <div style={{ color: 'rgba(255,255,255,0.4)', fontSize: '13px', fontWeight: '600', marginTop: '6px' }}>
                        Join battles of any size. Face players or bots, solo or in teams.
                    </div>
                </div>

                <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                    {/* Sort Dropdown */}
                    <div style={{ position: 'relative' }}>
                        <button 
                            onClick={() => setShowSort(!showSort)}
                            style={{ background: '#1c1c26', border: '1px solid rgba(255,255,255,0.05)', padding: '12px 20px', borderRadius: '8px', color: 'white', fontSize: '13px', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer', textTransform: 'none' }}
                        >
                            <span style={{ color: 'rgba(255,255,255,0.5)' }}>Sort by:</span> {sortMode === 'high' ? 'Price Descending' : sortMode === 'low' ? 'Price Ascending' : sortMode === 'newest' ? 'Newest' : 'Oldest'} <FaChevronDown size={10} style={{ transform: showSort ? 'rotate(180deg)' : 'none', transition: '0.2s', color: 'rgba(255,255,255,0.3)' }} />
                        </button>
                        
                        <AnimatePresence>
                            {showSort && (
                                <motion.div 
                                    initial={{ opacity: 0, y: 10 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    exit={{ opacity: 0, y: 10 }}
                                    style={{ position: 'absolute', top: 'calc(100% + 5px)', right: 0, width: '180px', background: '#1c1c26', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '8px', padding: '5px', zIndex: 1000, boxShadow: '0 10px 30px rgba(0,0,0,0.5)' }}
                                >
                                    {[
                                        { label: 'Price Descending', val: 'high' },
                                        { label: 'Price Ascending', val: 'low' },
                                        { label: 'Newest', val: 'newest' },
                                        { label: 'Oldest', val: 'oldest' }
                                    ].map(opt => (
                                        <div 
                                            key={opt.val}
                                            onClick={() => { setSortMode(opt.val); setShowSort(false); }}
                                            style={{ padding: '10px 15px', borderRadius: '4px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', color: sortMode === opt.val ? 'white' : 'rgba(255,255,255,0.5)', fontSize: '13px', fontWeight: '600', cursor: 'pointer', background: sortMode === opt.val ? 'rgba(255,255,255,0.05)' : 'transparent' }}
                                            onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.05)'}
                                            onMouseLeave={e => e.currentTarget.style.background = sortMode === opt.val ? 'rgba(255,255,255,0.05)' : 'transparent'}
                                        >
                                            {opt.label}
                                        </div>
                                    ))}
                                </motion.div>
                            )}
                        </AnimatePresence>
                    </div>

                    <button 
                        onClick={() => {
                            if (!user) {
                                if (window._addToast) window._addToast({ type: 'error', title: 'AUTH ERROR', message: 'Login to create a battle' });
                                return;
                            }
                            onCreateClick();
                        }}
                        style={{ background: '#ffb703', color: '#fff', border: 'none', padding: '12px 24px', borderRadius: '8px', fontSize: '14px', fontWeight: '900', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px', textTransform: 'uppercase', transition: 'all 0.2s', boxShadow: '0 4px 15px rgba(124, 58, 237, 0.2)' }}
                        onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 6px 20px rgba(124, 58, 237, 0.4)'; }}
                        onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 4px 15px rgba(124, 58, 237, 0.2)'; }}
                    >
                        <GiCrossedSwords size={16} /> CREATE BATTLE
                    </button>
                </div>
            </div>

            {/* CASES STYLE FILTER BAR */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '35px', maxWidth: '1200px', width: '100%', margin: '0 auto 35px auto', overflowX: 'auto', paddingBottom: '10px' }}>
                <button 
                    onClick={() => setActiveTab('active')}
                    style={{ background: activeTab === 'active' ? 'rgba(255,255,255,0.05)' : '#1c1c26', border: 'none', padding: '10px 20px', borderRadius: '8px', color: activeTab === 'active' ? 'white' : 'rgba(255,255,255,0.5)', fontSize: '14px', fontWeight: '800', display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', transition: 'all 0.2s', whiteSpace: 'nowrap' }}
                >
                    <FaBolt style={{ opacity: activeTab === 'active' ? 1 : 0.5, color: activeTab === 'active' ? '#ffb703' : 'inherit' }} /> Active
                </button>
                <button 
                    onClick={() => setActiveTab('history')}
                    style={{ background: activeTab === 'history' ? 'rgba(255,255,255,0.05)' : '#1c1c26', border: 'none', padding: '10px 20px', borderRadius: '8px', color: activeTab === 'history' ? 'white' : 'rgba(255,255,255,0.5)', fontSize: '14px', fontWeight: '800', display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', transition: 'all 0.2s', whiteSpace: 'nowrap' }}
                >
                    <FaSyncAlt style={{ opacity: activeTab === 'history' ? 1 : 0.5 }} /> History
                </button>
                <button 
                    onClick={() => setActiveTab('mine')}
                    style={{ background: activeTab === 'mine' ? 'rgba(255,255,255,0.05)' : '#1c1c26', border: 'none', padding: '10px 20px', borderRadius: '8px', color: activeTab === 'mine' ? 'white' : 'rgba(255,255,255,0.5)', fontSize: '14px', fontWeight: '800', display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', transition: 'all 0.2s', whiteSpace: 'nowrap' }}
                >
                    <FaUsers style={{ opacity: activeTab === 'mine' ? 1 : 0.5 }} /> My Battles
                </button>

                {/* Search */}
                <div style={{ position: 'relative', width: '220px', marginLeft: 'auto' }}>
                    <input 
                        type="text" 
                        value={searchQuery} 
                        onChange={(e) => setSearchQuery(e.target.value)} 
                        placeholder="Search battles..." 
                        style={{ width: '100%', background: '#1c1c26', border: '1px solid rgba(255,255,255,0.05)', padding: '10px 15px 10px 40px', borderRadius: '8px', color: 'white', fontSize: '13px', outline: 'none' }} 
                    />
                    <FaSearch style={{ position: 'absolute', left: '15px', top: '50%', transform: 'translateY(-50%)', color: 'rgba(255,255,255,0.3)', fontSize: '13px' }} />
                </div>
            </div>

            {/* Battle List */}
            <div className="cb-battles-list">
                {filteredBattles.length === 0 ? (
                    <div className="cb-empty-state" style={{ gridColumn: 'span 1', padding: '100px 0' }}>
                        <GiCrossedSwords style={{ fontSize: 42, opacity: 0.15, marginBottom: 12 }} />
                        <div style={{ fontWeight: 900, fontSize: 14, opacity: 0.4, textTransform: 'uppercase' }}>No battles found</div>
                        <div style={{ fontSize: 12, opacity: 0.25, marginTop: 6 }}>Try adjusting your filters or create one!</div>
                    </div>
                ) : filteredBattles.map(renderBattleRow)}
            </div>
        </div>
    );
};

/* --- CREATE VIEW --- */
const CreateView = ({ onBack, onCreate, onInspect, editData, user, openCaseSelector, showCaseSelector }) => {
    const [mode, setMode] = useState(editData?.mode || 'normal'); 
    const [slotsCount, setSlotsCount] = useState(editData?.playerCount || 2);
    const [selectedCases, setSelectedCases] = useState(editData?.cases ? editData.cases.map(c => ({...c, uniqueId: Math.random() + Math.random()})) : []);
    const [selectedPrefs, setSelectedPrefs] = useState(editData?.prefs || []);
    const [fundingEnabled, setFundingEnabled] = useState(false);
    const [fundingPercent, setFundingPercent] = useState(0);


    useEffect(() => {
        if (mode === 'team' && slotsCount < 4) setSlotsCount(4);
        if (mode === 'normal' && slotsCount > 4) setSlotsCount(4);
    }, [mode]);

    // Removed showModal state (now managed in parent and used with Portal)
    const [searchQuery, setSearchQuery] = useState(() => localStorage.getItem('battle_filter_search') || '');
    const [activeTab, setActiveTab] = useState(() => localStorage.getItem('battle_filter_tab') || 'Classic');
    const [minPrice, setMinPrice] = useState(() => localStorage.getItem('battle_filter_minp') || '');
    const [maxPrice, setMaxPrice] = useState(() => localStorage.getItem('battle_filter_maxp') || '');
    const [riskLevel, setRiskLevel] = useState(() => parseInt(localStorage.getItem('battle_filter_risk') || '0'));
    const [sortMode, setSortMode] = useState(() => localStorage.getItem('battle_filter_sort') || 'newest');
    
    const [showSort, setShowSort] = useState(false);
    const [favorites, setFavorites] = useState(() => {
        const saved = localStorage.getItem('case_favorites');
        return saved ? JSON.parse(saved) : [];
    });

    useEffect(() => {
        localStorage.setItem('battle_filter_search', searchQuery);
        localStorage.setItem('battle_filter_tab', activeTab);
        localStorage.setItem('battle_filter_minp', minPrice);
        localStorage.setItem('battle_filter_maxp', maxPrice);
        localStorage.setItem('battle_filter_risk', riskLevel.toString());
        localStorage.setItem('battle_filter_sort', sortMode);
    }, [searchQuery, activeTab, minPrice, maxPrice, riskLevel, sortMode]);
    
    useEffect(() => {
        localStorage.setItem('case_favorites', JSON.stringify(favorites));
    }, [favorites]);

    const toggleFavorite = (caseId) => {
        setFavorites(prev => prev.includes(caseId) ? prev.filter(x => x !== caseId) : [...prev, caseId]);
    };

    const togglePref = (p) => {
        setSelectedPrefs(prev => prev.includes(p) ? prev.filter(x => x !== p) : [...prev, p]);
    };

    const totalCost = selectedCases.reduce((acc, c) => acc + c.price, 0);

    const handleAddCase = (c) => {
        if (selectedCases.length >= 30) {
            if (window._addToast) window._addToast({ type: 'warning', title: 'LIMIT REACHED', message: 'Max 30 cases per battle' });
            return;
        }
        setSelectedCases([...selectedCases, { ...c, uniqueId: Date.now() + Math.random() }]);
    };

    const handleRemoveCase = (uid) => {
        setSelectedCases(selectedCases.filter(c => c.uniqueId !== uid));
    };

    const handleRemoveOneCase = (caseId) => {
        const idx = selectedCases.findLastIndex(c => c.id === caseId);
        if (idx !== -1) {
            const newCases = [...selectedCases];
            newCases.splice(idx, 1);
            setSelectedCases(newCases);
        }
    };

    const filteredCases = useMemo(() => {
        let result = MOCK_CASES.filter(c => {
            const matchesTab = activeTab === 'Favorites' ? favorites.includes(c.id) : (activeTab === 'Classic' ? (!c.category || c.category === 'Classic') : c.category === activeTab);
            const matchesSearch = c.name.toLowerCase().includes(searchQuery.toLowerCase());
            const matchesPrice = (!minPrice || c.price >= parseFloat(minPrice)) && (!maxPrice || c.price <= parseFloat(maxPrice));
            
            const r = getCaseRisk(c);
            const cLevel = r < 25 ? 1 : r < 50 ? 2 : r < 75 ? 3 : 4;
            const matchesRisk = riskLevel === 0 || cLevel === riskLevel;

            return matchesTab && matchesSearch && matchesPrice && matchesRisk;
        });

        if (sortMode === 'high') result.sort((a, b) => b.price - a.price);
        else if (sortMode === 'low') result.sort((a, b) => a.price - b.price);
        else if (sortMode === 'newest') result.sort((a, b) => a.id - b.id);
        else if (sortMode === 'oldest') result.sort((a, b) => a.id - b.id);

        return result;
    }, [searchQuery, activeTab, minPrice, maxPrice, riskLevel, sortMode, favorites]);

    const classicCount = MOCK_CASES.filter(c => !c.category || c.category === 'Classic').length;

    const handleShuffleCases = () => {
        setSelectedCases([...selectedCases].sort(() => Math.random() - 0.5));
    };

    const handleFinalCreate = () => {
        if (selectedCases.length === 0) {
            if (window._addToast) window._addToast({ type: 'warning', title: 'EMPTY BATTLE', message: 'Select some cases first' });
            return;
        }
        const initialSlots = Array(slotsCount).fill(null);
        initialSlots[0] = { id: 'u1', username: 'You', avatar: MOCK_AVATARS[0], totalValue: 0, unboxedItems: [] };
        
        /* NO AUTO BOTS - User will call them manually in OpeningView */
        /*
        for (let i = 1; i < slotsCount; i++) {
            const bot = MOCK_BOTS[i % MOCK_BOTS.length];
            initialSlots[i] = { id: 'bot-' + i, username: bot.username, avatar: bot.avatar, isBot: true, totalValue: 0, unboxedItems: [] };
        }
        */

        const isCrazy = selectedPrefs.includes('Crazy');
        const isJackpot = selectedPrefs.includes('Jackpot');

        onCreate({
            caseIds: selectedCases.map(c => c.id),
            mode: mode,
            playerCount: slotsCount,
            isPrivate: selectedPrefs.includes('Private'),
            isCrazy,
            prefs: selectedPrefs,
            usePromoBalance: false,
            fundingPercent: fundingEnabled ? fundingPercent : 0
        });
    };

    return (
        <div style={{ padding: '40px 60px 100px 60px', width: '100%', maxWidth: '1400px', margin: '0 auto' }}>
            {/* HEADER */}
            <div className="create-header-v4">
                <div onClick={onBack} className="b-back">
                    <FaChevronLeft/> CASE BATTLES
                </div>
                <div className="b-title">
                    <img src="/scrap.webp" style={{width: '24px'}} alt=""/> CREATE BATTLE
                </div>

                <button className="create-btn-top" onClick={handleFinalCreate}>
                    <FaPlus/> CREATE BATTLE <img src="/scrap.webp" className="scrap-ico-sm" style={{marginLeft: '10px'}}/> {(totalCost + (totalCost * (slotsCount - 1) * (fundingPercent / 100))).toFixed(2)}
                </button>
            </div>

            {/* TOP STRIP */}
            <div className="top-config-strip">
                <div className="round-hex-box">
                    {selectedCases.length}
                </div>
                <div className="hex-controls">
                    <button className="hex-btn"><FaArrowUp size={10}/></button>
                    <button className="hex-btn"><FaArrowDown size={10}/></button>
                    <button className="hex-btn" onClick={handleShuffleCases}><FaRandom size={10}/></button>
                </div>

                <div className="strip-cases-v4">
                    {selectedCases.length === 0 ? (
                        <div style={{ color: 'rgba(255,255,255,0.1)', fontSize: '11px', fontWeight: '900', textTransform: 'uppercase' }}>Select cases below to build your battle round by round... {selectedCases.length}/30</div>
                    ) : (
                        Object.values(selectedCases.reduce((acc, c) => {
                            if (!acc[c.id]) acc[c.id] = { ...c, count: 0 };
                            acc[c.id].count++;
                            return acc;
                        }, {})).map((c, i) => (
                            <motion.div initial={{scale:0.8}} animate={{scale:1}} className={`mini-case-v4 active`} key={c.id}>
                                <img src={c.img} alt=""/>
                                {c.count > 1 && <div className="mini-qty-badge">x{c.count}</div>}
                            </motion.div>
                        ))
                    )}
                </div>

                <div className="strip-side-btns">
                    <button className="strip-btn grey" onClick={() => setSelectedCases([])}><FaTrash/> RESET ALL</button>
                </div>
            </div>

            {/* MIDDLE SECTION */}
            <div className="create-grid-v4">
                {/* SIDEBAR: PLAYERS */}
                <div className="sidebar-v4">
                    <div className="section-lbl-v4"><FaUsers color={BRAND_PURPLE}/> SELECT PLAYERS</div>
                    <div className="player-mode-toggle">
                        <button className={`toggle-btn ${mode === 'normal' ? 'active' : ''}`} onClick={() => setMode('normal')}>NORMAL</button>
                        <button className={`toggle-btn ${mode === 'team' ? 'active' : ''}`} onClick={() => setMode('team')}>TEAM</button>
                    </div>

                    <div className="slots-list-v4">
                        {(mode === 'normal' ? [2, 3, 4] : [4, 6]).map(n => (
                            <div key={n} className={`slot-row-v4 ${slotsCount === n ? 'active' : ''}`} onClick={() => setSlotsCount(n)}>
                                {mode === 'normal' ? (
                                    n === 2 ? '1v1' : n === 3 ? '1v1v1' : '1v1v1v1'
                                ) : `${n/2}v${n/2} TEAM`}
                            </div>
                        ))}
                    </div>
                </div>

                {/* MAIN: OPTIONS */}
                <div className="main-settings-v4">
                    <div className="section-lbl-v4"><FaHistory color={BRAND_PURPLE}/> BATTLE OPTIONS</div>
                    <div className="options-grid-v4">
                        {[
                            { id: 'Fast Mode', icon: <FaBolt/>, name: 'FAST MODE' },
                            { id: 'Crazy', icon: <FaSyncAlt/>, name: 'CRAZY' },
                            { id: 'Jackpot', icon: <img src="/scrap.webp" style={{width: '20px'}} alt=""/>, name: 'JACKPOT' },

                            { id: 'Private', icon: <FaLock/>, name: 'PRIVATE' }
                        ].map(opt => (
                            <div key={opt.id} className={`opt-card-v4 ${selectedPrefs.includes(opt.id) ? 'active' : ''}`} onClick={() => togglePref(opt.id)}>
                                <div className="opt-ico">{opt.icon}</div>
                                <div className="opt-name">{opt.name}</div>
                            </div>
                        ))}
                    </div>

                    {!(user?.roles?.includes('streamer') || user?.roles?.includes('Streamer') || user?.role === 'streamer') && (
                        <div className="funding-section-v4">
                            <div className="funding-header">
                                <div className="f-title"><FaHandHoldingHeart color="#22c55e"/> PARTIAL SLOT FUNDING</div>
                                <div className={`f-toggle ${fundingEnabled ? 'active' : ''}`} onClick={() => setFundingEnabled(!fundingEnabled)}>
                                    <div className="toggle-thumb"/>
                                </div>
                            </div>
                            
                            <div className={`funding-controls ${fundingEnabled ? '' : 'disabled'}`}>
                                <div className="funding-slider-wrap">
                                    <input 
                                        type="range" 
                                        min="0" 
                                        max="100" 
                                        step="10" 
                                        value={fundingPercent} 
                                        onChange={(e) => setFundingPercent(parseInt(e.target.value))}
                                        className="funding-range"
                                        style={{ '--fill': `${fundingPercent}%` }}
                                    />
                                    <div className="funding-ticks">
                                        {[0, 20, 40, 60, 80, 100].map(v => (
                                            <div key={v} className="tick">
                                                <div className="tick-mark"/>
                                                <div className="tick-lbl">{v}%</div>
                                            </div>
                                        ))}
                                    </div>
                                </div>

                                <div className="funding-stats-row">
                                    <div className="f-stat-item">
                                        <div className="f-stat-ico"><FaHandHoldingHeart color="#22c55e"/></div>
                                        <div className="f-stat-details">
                                            <div className="v">{fundingPercent}%</div>
                                            <div className="l">FUNDING</div>
                                        </div>
                                    </div>
                                    <div className="f-stat-item">
                                        <div className="f-stat-ico"><FaGem color="#22c55e"/></div>
                                        <div className="f-stat-details">
                                            <div className="v">{(totalCost * (slotsCount - 1) * (fundingPercent / 100)).toFixed(2)}</div>
                                            <div className="l">SPONSORED</div>
                                        </div>
                                    </div>
                                    <div className="f-stat-item">
                                        <div className="f-stat-ico"><FaGem color="#a855f7"/></div>
                                        <div className="f-stat-details">
                                            <div className="v">{fundingPercent === 100 ? 'FREE' : (totalCost * (1 - (fundingPercent / 100))).toFixed(2)}</div>
                                            <div className="l">NEW PRICE</div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* BOTTOM SUMMARY */}
            <div className="bottom-summary-v4">
                <div className="stat-pill">
                    <FaBoxOpen color={BRAND_PURPLE}/> ROUNDS: {selectedCases.length}/30
                </div>
                <div className="stat-pill dark">
                    BATTLE VALUE: <img src="/scrap.webp" className="scrap-ico-sm" style={{marginLeft: '10px'}}/> {totalCost.toFixed(2)}
                </div>
                {fundingPercent > 0 && (
                    <div className="stat-pill" style={{ background: 'rgba(124, 58, 237, 0.1)', border: '1px solid rgba(124, 58, 237, 0.2)', color: '#c084fc' }}>
                        YOUR TOTAL: <img src="/scrap.webp" className="scrap-ico-sm" style={{marginLeft: '10px'}}/> {(totalCost + (totalCost * (slotsCount - 1) * (fundingPercent / 100))).toFixed(2)}
                    </div>
                )}
            </div>

            {/* SELECTED CASE CARDS GRID */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '20px', marginBottom: '100px' }}>
                <motion.div whileHover={{y: -5}} className="add-case-huge" onClick={() => openCaseSelector(true)} style={{ height: '260px', background: 'rgba(255,255,255,0.02)', border: '2px dashed rgba(255,255,255,0.05)', borderRadius: '20px', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', gap: '15px' }}>
                    <div style={{ width: '50px', height: '50px', background: 'rgba(255,255,255,0.03)', borderRadius: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '24px', color: 'rgba(255,255,255,0.2)' }}>+</div>
                    <span style={{ fontSize: '10px', fontWeight: '950', opacity: 0.3, letterSpacing: '1px' }}>ADD CASE</span>
                </motion.div>

                <AnimatePresence>
                    {Object.values(selectedCases.reduce((acc, c) => {
                        if (!acc[c.id]) acc[c.id] = { ...c, count: 0 };
                        acc[c.id].count++;
                        return acc;
                    }, {})).map((c) => (
                        <motion.div 
                            initial={{scale:0.9, opacity:0}} 
                            animate={{scale:1, opacity:1}} 
                            exit={{scale:0.9, opacity:0}} 
                            key={c.id} 
                            className="case-card-huge" 
                            onContextMenu={(e) => {
                                e.preventDefault();
                                onInspect(c);
                            }}
                            style={{ height: '260px', background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.05)', borderRadius: '20px', padding: '20px', position: 'relative', display: 'flex', flexDirection: 'column', alignItems: 'center', cursor: 'help' }}
                        >
                            <div style={{ background: 'rgba(0,0,0,0.3)', padding: '5px 12px', borderRadius: '10px', fontSize: '12px', fontWeight: '950', display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '15px' }}>
                                <img src="/scrap.webp" className="scrap-ico-sm" alt=""/> {c.price.toFixed(2)}
                            </div>
                            <img src={c.img} style={{ width: '80px', height: '80px', objectFit: 'contain', marginBottom: '10px', filter: 'drop-shadow(0 10px 15px rgba(0,0,0,0.5))' }} alt=""/>
                            <div style={{ fontSize: '13px', fontWeight: '950', textAlign: 'center', marginBottom: '15px' }}>{c.name}</div>
                            
                            <div style={{ background: 'rgba(255,255,255,0.03)', borderRadius: '10px', display: 'flex', alignItems: 'center', overflow: 'hidden' }}>
                                <button style={{ border: 'none', background: 'transparent', padding: '10px 15px', color: 'rgba(255,255,255,0.3)', cursor: 'pointer' }} onClick={() => handleRemoveOneCase(c.id)}>-</button>
                                <span style={{ padding: '0 10px', fontSize: '12px', fontWeight: '950' }}>{c.count}</span>
                                <button style={{ border: 'none', background: 'transparent', padding: '10px 15px', color: 'rgba(255,255,255,0.3)', cursor: 'pointer' }} onClick={() => handleAddCase(c)}>+</button>
                            </div>
                        </motion.div>
                    ))}
                </AnimatePresence>
            </div>

            {showCaseSelector && createPortal(
                    <div className="modal-overlay" onClick={() => openCaseSelector?.(false)}>
                        <motion.div initial={{scale:0.95, opacity:0}} animate={{scale:1, opacity:1}} exit={{scale:0.95, opacity:0}} className="modal-box-v2" onClick={e => e.stopPropagation()}>
                             {/* Modal Header */}
                             <div className="modal-header-v2">
                                <div className="header-left">
                                    <FaBriefcase className="header-ico" />                                     
                                    <span>Add cases</span>
                                </div>
                                <FaTimes className="close-ico" onClick={() => openCaseSelector?.(false)}/>
                             </div>


                             {/* Tabs Bar */}
                             <div className="modal-tabs-bar">
                                 <div className={`tab-item ${activeTab === 'Classic' ? 'active' : ''}`} onClick={() => setActiveTab('Classic')}><FaBriefcase/> Classic <span className="cnt">{classicCount}</span></div>
                                 <div className={`tab-item ${activeTab === 'Favorites' ? 'active' : ''}`} onClick={() => setActiveTab('Favorites')}><FaHeart/> Favorites <span className="cnt">{favorites.length}</span></div>
                                 
                                 <div className="search-wrap-v2">
                                     <input type="text" placeholder="Enter case name..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} />
                                     <FaSearch className="s-ico" />
                                 </div>
                             </div>

                             {/* Filter Bar */}
                             <div className="modal-filters-v2">
                                 <div className="price-inputs-v2">
                                     <div className="p-box">
                                         <img src="/scrap.webp" className="scrap-ico-sm"/>
                                         <input type="number" placeholder="0.00" value={minPrice} onChange={(e) => setMinPrice(e.target.value)} className="filter-num-input" />
                                     </div>
                                     <div className="p-box">
                                         <img src="/scrap.webp" className="scrap-ico-sm"/>
                                         <input type="number" placeholder="9999.99" value={maxPrice} onChange={(e) => setMaxPrice(e.target.value)} className="filter-num-input" />
                                     </div>
                                 </div>
                                 
                                 <div style={{ position: 'relative' }}>


                                    <div className="cb-filter-group">
                                     <div className="filter-dropdown-v2" onClick={() => setShowSort(!showSort)}>
                                         {sortMode === 'high' ? 'PRICE: HIGH TO LOW' : sortMode === 'low' ? 'PRICE: LOW TO HIGH' : sortMode === 'newest' ? 'NEWEST' : 'OLDEST'} 
                                         <FaChevronDown style={{opacity: 0.3, fontSize: '10px', transform: showSort ? 'rotate(180deg)' : 'none', transition: '0.2s'}}/>
                                     </div>
                                     <AnimatePresence>
                                         {showSort && (
                                             <motion.div initial={{opacity:0, y:10}} animate={{opacity:1, y:0}} exit={{opacity:0, y:10}} className="dropdown-menu-v2">
                                                 {[
                                                     {label: 'PRICE: HIGH TO LOW', val: 'high'},
                                                     {label: 'PRICE: LOW TO HIGH', val: 'low'},
                                                     {label: 'NEWEST', val: 'newest'},
                                                     {label: 'OLDEST', val: 'oldest'}
                                                 ].map(opt => (
                                                     <div key={opt.val} className={`menu-opt ${sortMode === opt.val ? 'active' : ''}`} onClick={() => { setSortMode(opt.val); setShowSort(false); }}>
                                                         {opt.label}
                                                         {sortMode === opt.val && <FaCheck size={10} style={{opacity: 0.5}}/>}
                                                     </div>
                                                 ))}
                                             </motion.div>
                                         )}
                                     </AnimatePresence>
                                 </div>
                                 </div>
                                 
                                  <div className="risk-segmented-control">
                                       <div className="risk-label-group" onClick={() => setRiskLevel(0)}>
                                           <span className="tiny-lbl">RISK LEVEL</span>
                                           <div className="current-risk-val" style={{ color: riskLevel === 0 ? 'var(--text-muted)' : riskLevel === 1 ? '#22c55e' : riskLevel === 2 ? '#ffb703' : riskLevel === 3 ? '#ffb703' : '#ef4444' }}>
                                               {riskLevel === 0 ? 'ALL' : riskLevel === 1 ? 'LOW' : riskLevel === 2 ? 'MID' : riskLevel === 3 ? 'BIG' : 'EXTREME'}
                                               {riskLevel > 0 && <span className="risk-reset-mini">RESET</span>}
                                           </div>
                                       </div>
                                       <div className="risk-segments">
                                           {[1,2,3,4].map(i => (
                                               <div 
                                                   key={i} 
                                                   className={`risk-seg s${i} ${riskLevel === i ? 'active' : ''}`}
                                                   onClick={() => setRiskLevel(i)}
                                               >
                                                   <div className="seg-fill" />
                                               </div>
                                           ))}
                                       </div>
                                   </div>
                              </div>

                             {/* Cases Scrollable Area */}
                             <div className="modal-cases-scroll">
                                {filteredCases.map((c, i) => {
                                    const caseQty = selectedCases.filter(sc => sc.id === c.id).length;
                                    const isFav = favorites.includes(c.id);
                                    return (
                                        <div key={c.id} className="case-card-v3">
                                            <div className="card-top">
                                                <FaHeart className={`fav-ico ${isFav ? 'active' : ''}`} onClick={() => toggleFavorite(c.id)} />
                                                <div className="price-tag-scrap">
                                                    <img src="/scrap.webp" className="scrap-ico-sm" style={{marginRight: '6px'}}/> {c.price.toFixed(2)}
                                                </div>
                                            </div>
                                            
                                            <div className="card-img-main" onClick={(e) => { e.stopPropagation(); handleAddCase(c); }}>
                                                <img src={c.img} alt=""/>
                                            </div>

                                            <div className="card-name-v3">{c.name}</div>

                                            <div className="card-actions-v3">
                                                <button className="inspect-btn-v3" onClick={() => onInspect(c)}><FaSearch/> INSPECT</button>
                                                
                                                {caseQty > 0 ? (
                                                    <div className="qty-counter-v3">
                                                        <button onClick={(e) => { e.stopPropagation(); handleRemoveOneCase(c.id); }}>-</button>
                                                        <span>{caseQty}</span>
                                                        <button onClick={(e) => { e.stopPropagation(); handleAddCase(c); }}>+</button>
                                                    </div>
                                                ) : (
                                                    <button className="add-btn-v3" onClick={(e) => { e.stopPropagation(); handleAddCase(c); }}>
                                                        ADD CASE <FaBriefcase/>
                                                    </button>
                                                )}
                                            </div>

                                            <div className="card-risk-v3">
                                                <div className="track">
                                                    <div className="fill" style={{width: `${getCaseRisk(c)}%`, background: getCaseRisk(c) > 70 ? '#ef4444' : getCaseRisk(c) > 30 ? '#ffb703' : '#22c55e'}} />
                                                    <div className="thumb" style={{left: `${getCaseRisk(c)}%` }} />
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}
                              </div>

                              {/* Modal Footer */}
                              <div className="modal-footer-v2">
                                 <div className="footer-stats">
                                     <div className="stat">ROUNDS: <b>{selectedCases.length}</b></div>
                                     <div className="stat">COST: <img src="/scrap.webp" className="scrap-ico-sm" style={{marginLeft: '10px'}}/> <b>{totalCost.toFixed(2)}</b></div>
                                 </div>
                                 <button className="btn-done-purp" onClick={() => openCaseSelector?.(false)}>DONE</button>
                              </div>
                         </motion.div>
                     </div>,
                     document.body
                 )}
        </div>
    );
};

const JackpotWinnerSpinner = ({ slots, winnerId, onDone, isCrazy }) => {
    const reelRef = useRef(null);
    const [isDone, setIsDone] = useState(false);
    const winIdx = 4; // Stop on the 5th iteration of the pattern
    const duration = 6000;

    const totalPot = useMemo(() => slots.reduce((acc, s) => acc + (s?.totalValue || 0), 0), [slots]);
    const COL_PALETTE = [
        { c: '#ffb703' }, { c: '#3b82f6' }, { c: '#22c55e' }, { c: '#ef4444' }, { c: '#a855f7' }, { c: '#ffb703' },
    ];

    // Generate segments based on player percentages
    const segments = useMemo(() => {
        const weights = (slots || []).map(p => 1 / ((p?.totalValue || 0) + 0.01));
        const totalWeight = weights.reduce((acc, w) => acc + w, 0);

        return (slots || []).map((s, i) => {
            let pct;
            if (isCrazy && totalPot > 0) {
                const myWeight = 1 / ((s?.totalValue || 0) + 0.01);
                pct = totalWeight > 0 ? (myWeight / totalWeight) * 100 : (100 / slots.length);
            } else {
                pct = totalPot > 0 ? ((s?.totalValue || 0) / totalPot) * 100 : (100 / slots.length);
            }
            return {
                id: s.id,
                color: COL_PALETTE[i % COL_PALETTE.length].c,
                pct
            };
        });
    }, [slots, totalPot, isCrazy]);

    useLayoutEffect(() => {
        const reel = reelRef.current;
        if (!reel) return;

        // Pattern repeat for spinning look
        const patternWidth = 1000; // 1000px per pattern iteration
        
        // Find winner's relative position in one pattern
        let winnerXInPattern = 0;
        const winnerSeg = segments.find(s => s.id === winnerId);
        if (winnerSeg) {
            let offset = 0;
            for (const s of segments) {
                if (s.id === winnerId) {
                    // Pick a random spot between 5% and 95% of the segment's width to look realistic
                    const randomPos = 0.05 + (Math.random() * 0.9);
                    winnerXInPattern = offset + ((s.pct / 100) * patternWidth * randomPos);
                    break;
                }
                offset += (s.pct * patternWidth / 100);
            }
        }

        const containerW = reel.parentElement.offsetWidth;
        const centerShift = containerW / 2;
        const targetX = centerShift - (winIdx * patternWidth) - winnerXInPattern;

        reel.style.transition = 'none';
        reel.style.transform = 'translateX(0px)';

        setTimeout(() => {
            const r = reelRef.current;
            if (!r) return;
            r.style.transition = `transform ${duration / 1000}s cubic-bezier(0.1, 0.5, 0, 1.0)`;
            r.style.transform = `translateX(${targetX}px)`;

            // Ticking Sound
            let elapsed = 0;
            const playTickLoop = () => {
                if (elapsed >= duration) return;
                const a = new Audio('/tick.mp3');
                a.volume = 0.6;
                a.play().catch(() => {});
                
                const progress = elapsed / duration;
                const nextTick = 40 + (Math.pow(progress, 2) * 500); // Decelerates
                elapsed += nextTick;
                setTimeout(playTickLoop, nextTick);
            };
            playTickLoop();
        }, 100);

        const doneTimer = setTimeout(() => {
            setIsDone(true);
            setTimeout(onDone, 1500);
        }, duration + 200);

        return () => clearTimeout(doneTimer);
    }, []);

    return (
        <div className="jackpot-final-spinner-v4">
            <div className="jfs-indicator-top">▼</div>
            <div className="jfs-indicator-bottom">▲</div>
            <div className="jfs-indicator-line" />
            <div className="jfs-reel" ref={reelRef}>
                {Array.from({ length: 10 }).map((_, iteration) => (
                    <div key={iteration} className="jfs-pattern" style={{ width: '1000px', display: 'flex', flexShrink: 0 }}>
                        {segments.map((seg, i) => {
                            const player = slots[i];
                            return (
                                <div 
                                    key={i} 
                                    className="jfs-segment"
                                    style={{ 
                                        width: `${seg.pct * 10}px`, // 100% = 1000px
                                        height: '100%', 
                                        background: seg.color,
                                        borderRight: '1px solid rgba(0,0,0,0.5)',
                                        position: 'relative',
                                        overflow: 'hidden',
                                        boxShadow: 'inset 0 0 20px rgba(0,0,0,0.2)'
                                    }} 
                                >
                                    <div className="jfs-seg-inner">
                                        <div className="jfs-avatar-box">
                                            <img src={player?.avatar || 'https://api.dicebear.com/7.x/pixel-art/svg?seed=placeholder'} alt="" />
                                        </div>
                                        <div className="jfs-pct-badge">{seg.pct.toFixed(2)}%</div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                ))}
            </div>
            {isDone && <div className="jfs-winner-flash" />}
        </div>
    );
};

const BattleSpinner = ({ targetItem, items, onComplete, isFast, startDelay = 0 }) => {
    const reelRef = useRef(null);
    const itemDim = 110;
    const winIdx = 48;
    const [isDone, setIsDone] = useState(false);
    const duration = isFast ? 1400 : 5500;

    const reelItems = useMemo(() => {
        if (!items || items.length === 0) {
            // Fallback if items are missing: repeat the target item
            return [...Array(winIdx + 11)].map(() => targetItem);
        }
        const pool = [...Array(winIdx)].map(() => items[Math.floor(Math.random() * items.length)]);
        pool.push(targetItem); // placed at exactly winIdx â€” matches where the animation stops
        const fillers = [...Array(10)].map(() => items[Math.floor(Math.random() * items.length)]);
        return [...pool, ...fillers];
    }, [targetItem, items]);

    useLayoutEffect(() => {
        const reel = reelRef.current;
        if (!reel) return;

        reel.style.transition = 'none';
        reel.style.transform = 'translateX(0px)';

        let tickTimer;
        let startTimer;
        let doneTimer;

        startTimer = setTimeout(() => {
            const r = reelRef.current;
            if (!r || !r.parentElement) return;

            const containerW = r.parentElement.offsetWidth;
            const centerShift = (containerW / 2) - (itemDim / 2);
            const targetX = centerShift - (winIdx * itemDim);

            r.style.transition = `transform ${duration / 1000}s cubic-bezier(0.12, 0.72, 0.08, 1)`;
            // eslint-disable-next-line no-unused-expressions
            r.offsetWidth; 

            const randomOffset = ((Math.random() - 0.5) * 45); // Slightly more jitter for realism
            r.style.transform = `translateX(${targetX + randomOffset}px)`;

            // SFX Loop
            let elapsed = 0;
            const playTickLoop = () => {
                if (elapsed >= duration) return;
                const a = new Audio('/tick.mp3');
                a.volume = isFast ? 0.3 : 0.5;
                a.play().catch(() => {});
                
                const progress = elapsed / duration;
                // Faster ticks for fast mode, normal for slow
                const baseTick = isFast ? 30 : 60;
                const curve = isFast ? 0.6 : 0.4;
                const nextTick = baseTick + (Math.pow(progress, curve) * (isFast ? 150 : 450));
                
                elapsed += nextTick;
                tickTimer = setTimeout(playTickLoop, nextTick);
            };
            playTickLoop();

            doneTimer = setTimeout(() => {
                const rr = reelRef.current;
                if (rr && rr.parentElement) {
                    const cW = rr.parentElement.offsetWidth;
                    const cShift = (cW / 2) - (itemDim / 2);
                    const finalX = cShift - (winIdx * itemDim);
                    
                    rr.style.transition = 'transform 0.6s cubic-bezier(0.2, 0.9, 0.3, 1.25)';
                    // eslint-disable-next-line no-unused-expressions
                    rr.offsetWidth;
                    rr.style.transform = `translateX(${finalX}px)`;
                }
                setIsDone(true);
                setTimeout(onComplete, isFast ? 350 : 1400);
            }, duration);

        }, 50 + startDelay);

        let rafId;
        const updateActiveItem = () => {
            const r = reelRef.current;
            if (r) {
                const style = window.getComputedStyle(r);
                if (style.transform && style.transform !== 'none') {
                    const matrix = new DOMMatrix(style.transform);
                    const xPos = matrix.m41;
                    const pRect = r.parentElement.getBoundingClientRect();
                    const containerW = pRect.width;
                    const index = (containerW / 2 - itemDim / 2 - xPos) / itemDim;
                    const activeIdx = Math.max(0, Math.round(index));
                    
                    Array.from(r.children).forEach((child, i) => {
                        if (i === activeIdx) {
                            if (!child.classList.contains('center-active')) child.classList.add('center-active');
                        } else {
                            if (child.classList.contains('center-active')) child.classList.remove('center-active');
                        }
                    });
                }
            }
            rafId = requestAnimationFrame(updateActiveItem);
        };
        rafId = requestAnimationFrame(updateActiveItem);

        return () => {
            clearTimeout(startTimer);
            clearTimeout(doneTimer);
            clearTimeout(tickTimer);
            cancelAnimationFrame(rafId);
        };
    }, []);

    const winnerColor = targetItem?.color || '#ffb703';

    return (
        <div
            className="bspin-col-wrap horizontal"
            style={isDone ? { '--win-color': winnerColor, '--win-glow': `${winnerColor}80` } : undefined}
        >
            <div
                className={`bspin-reel-horz ${isDone ? 'done' : ''}`}
                ref={reelRef}
            >
                {reelItems.map((item, i) => {
                    const isWin = isDone && i === winIdx;
                    const color = item.color || '#ffb703';
                    return (
                        <div key={i} className={`bspin-item-h ${isWin ? 'is-win' : ''}`} style={{ width: `${itemDim}px` }}>
                            <motion.img
                                src={item.img}
                                alt=""
                                animate={isWin ? { scale: [1, 1.25, 1.12], y: -8 } : { scale: 1, y: 0 }}
                                transition={isWin ? { duration: 0.5, ease: [0.2, 0.8, 0.3, 1.2], times: [0, 0.55, 1] } : { duration: 0.15 }}
                                style={{
                                    filter: isWin ? `drop-shadow(0 0 18px ${color}) drop-shadow(0 2px 6px rgba(0,0,0,0.6))` : undefined
                                }}
                            />
                            <AnimatePresence>
                                {isWin && (
                                    <motion.div 
                                        initial={{ opacity: 0, y: 10 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        transition={{ delay: 0.2, duration: 0.3 }}
                                        style={{ 
                                            position: 'absolute', 
                                            bottom: '2px', 
                                            textAlign: 'center', 
                                            width: '100%',
                                            display: 'flex',
                                            flexDirection: 'column',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            gap: '1px',
                                            textShadow: '0 2px 4px rgba(0,0,0,0.8)'
                                        }}
                                    >
                                        <div style={{ fontSize: '9px', fontWeight: '900', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', width: '90%', color }}>{item.name}</div>
                                        <div style={{ fontSize: '10px', fontWeight: '900', color: '#ffb703', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '3px' }}>
                                            <img src="/scrap.webp" alt="" style={{ width: '10px', height: '10px', filter: 'none', objectFit: 'contain' }} />
                                            {item.price?.toFixed(2)}
                                        </div>
                                    </motion.div>
                                )}
                            </AnimatePresence>
                        </div>
                    );
                })}
            </div>
            {isDone && <div className="win-halo-v4" style={{ background: winnerColor, opacity: 0.2 }} />}
        </div>
    );
};



/* --- OPENING VIEW v4 --- */
const TieBreakerSpin = ({ type, winnerIds, slots }) => {
    const isTeam = type === 'team';
    const winIdsSet = new Set(winnerIds);
    const halfway = Math.ceil(slots.length / 2);
    
    // Determine which side won for team mode
    const teamAWon = isTeam && slots.slice(0, halfway).some(s => winIdsSet.has(s.id));
    
    return (
        <div className="tie-breaker-overlay">
            <motion.div 
                initial={{ scale: 0.5, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                className="tie-breaker-content"
            >
                <div className="tb-title">TIE BREAKER!</div>
                <div className="tb-sub">50/50 ROLL FOR THE WINNER</div>
                
                <div className="tb-spinner-box">
                    <motion.div 
                        className="tb-coin"
                        animate={{ 
                            rotateY: [0, 1800, teamAWon || (!isTeam && winIdsSet.has(slots[0]?.id)) ? 0 : 180],
                        }}
                        transition={{ duration: 4, ease: [0.45, 0.05, 0.55, 0.95] }}
                    >
                        <div className="tb-side tb-front" style={{ background: COL_PALETTE[0].c }}>
                            {isTeam ? 'TEAM 1' : slots[0]?.username[0]}
                        </div>
                        <div className="tb-side tb-back" style={{ background: COL_PALETTE[1].c }}>
                            {isTeam ? 'TEAM 2' : slots[1]?.username[0]}
                        </div>
                    </motion.div>
                </div>
                
                <div className="tb-winner-text">
                    {isTeam ? (teamAWon ? 'TEAM 1 WINS!' : 'TEAM 2 WINS!') : 'A WINNER IS CHOSEN!'}
                </div>
            </motion.div>
        </div>
    );
};

const BattleOpeningView = ({ battleId, battles, updateBattle, onBack, onComplete, onInspect }) => {
    const { user } = useContext(UserContext);
    const { socket } = useContext(ChatContext);
    const [round, setRound] = useState(0);
    const [isSpinning, setIsSpinning] = useState(false);
    const [showJackpotFinal, setShowJackpotFinal] = useState(false);
    const [showTieBreaker, setShowTieBreaker] = useState(false);
    const [tieBreakerData, setTieBreakerData] = useState(null);
    const [jackpotWinnerColor, setJackpotWinnerColor] = useState(null);
    const [currentResults, setCurrentResults] = useState([]);
    
    const battle = battles.find(b => b.id === battleId);
    
    const COL_PALETTE = useMemo(() => [
        { c: '#ffb703', g: 'rgba(124, 58, 237, 0.35)' },
        { c: '#3b82f6', g: 'rgba(59, 130, 246, 0.35)' },
        { c: '#22c55e', g: 'rgba(34, 197, 94, 0.35)' },
        { c: '#ef4444', g: 'rgba(239, 68, 68, 0.35)' },
    ], []);
    
    const handleAddBot = () => {
        if (socket) socket.emit('call_bot_to_battle', { battleId });
    };

    const handleJoinBattle = (slotIndex) => {
        if (!user) {
            if (window._addToast) window._addToast({ type: 'error', title: 'AUTH ERROR', message: 'You must be logged in to join a battle' });
            return;
        }
        if (socket) socket.emit('join_case_battle', { battleId, slotIndex });
    };

    useEffect(() => {
        window._isBattleOpening = true;
        return () => {
            window._isBattleOpening = false;
            if (window._pendingBalanceUpdate) {
                window.dispatchEvent(new CustomEvent('balanceUpdate', { 
                    detail: { ...window._pendingBalanceUpdate, isFinal: true } 
                }));
                window._pendingBalanceUpdate = null;
            }
        };
    }, []);

    useEffect(() => {
        if (!socket || !battle) return;

        const onBattleUpdated = (updatedBattle) => {
            if (updatedBattle.id === battleId) updateBattle(battleId, updatedBattle);
        };

        const onRoundResult = (data) => {
            const { roundIdx, results } = data;
            setRound(roundIdx);
            setIsSpinning(true);
            const itemResults = (results || []).sort((a,b) => a.playerIdx - b.playerIdx).map(r => r.item);
            setCurrentResults(itemResults);

            // Wait for the spin animation to finish before showing items in history
            // Standard: 5.5s spin + 1.4s settle, Fast: 1.4s spin + 0.35s settle
            const isFast = battle.prefs && battle.prefs.includes('Fast Mode');
            const revealDelay = isFast ? 2000 : 6500;

            setTimeout(() => {
                const updatedBattle = { ...battle };
                results.forEach(res => {
                    const player = updatedBattle.slots[res.playerIdx];
                    if (player) {
                        if (!player.unboxedItems) player.unboxedItems = [];
                        player.unboxedItems[roundIdx] = [res.item];
                        player.totalValue = (player.totalValue || 0) + res.item.price;
                    }
                });
                updateBattle(battleId, updatedBattle);
            }, revealDelay);
        };

        const onTieBreaker = (data) => {
            setTieBreakerData(data);
            setShowTieBreaker(true);
        };

        const onBattleCompleted = (finalBattle) => {
            updateBattle(battleId, finalBattle);
            
            const isJackpot = finalBattle.isJackpot || (finalBattle.prefs && finalBattle.prefs.includes('Jackpot'));
            if (isJackpot) {
                // Show the visual jackpot spinner
                setShowJackpotFinal(true);
                // Set up the callback for when the spinner finishes
                window._finalizeJackpot = () => {
                    setTimeout(() => {
                        onComplete();
                    }, 2500); // Small pause after winner is shown
                };
            } else {
                // Normal transition
                setTimeout(() => onComplete(), 4000);
            }
        };

        socket.on('battle_updated', onBattleUpdated);
        socket.on('battle_started', onBattleUpdated);
        socket.on('battle_round_result', onRoundResult);
        socket.on('battle_tie_breaker', onTieBreaker);
        socket.on('battle_completed', onBattleCompleted);

        return () => {
            socket.off('battle_updated', onBattleUpdated);
            socket.off('battle_started', onBattleUpdated);
            socket.off('battle_round_result', onRoundResult);
            socket.off('battle_tie_breaker', onTieBreaker);
            socket.off('battle_completed', onBattleCompleted);
        };
    }, [socket, battleId, battle]);

    if (!battle) {
        return (
            <div className="cb-loading-state">
                <div className="spinner-cb" />
                <p>Loading Battle Data...</p>
            </div>
        );
    }

    const canStart = battle.slots && battle.slots.every(s => s !== null);



    const onSpinnerDone = () => {};

    return (
        <div className="opening-view-v4">
            {/* TOP BAR */}
            <div className="opening-nav-v4">
                <div className="nav-left">
                    <div className="b-back" onClick={onBack}><FaChevronLeft/> CASE BATTLES</div>
                    <div className="battle-id-tag">BATTLE #{battleId.slice(-6)}</div>
                </div>
                <div className="nav-center">
                    <div className="mode-pill-v4">
                        {(() => {
                            const n = (battle.slots || []).length;
                            return battle.mode === 'team' ? `${n/2}V${n/2}` : (n === 2 ? '1V1' : (n === 3 ? '1V1V1' : (n === 4 ? '1V1V1V1' : `${n}P`)));
                        })()}
                    </div>
                </div>
                <div className="nav-right">
                    <div style={{ display: 'flex', gap: '10px' }}>
                        <button className="battle-action-btn-v4" title="Copy Link" onClick={() => {
                            const link = `${window.location.origin}${window.location.pathname}#/battle/${battle.id}`;
                            navigator.clipboard.writeText(link);
                            if (window._addToast) window._addToast({ type: 'success', title: 'LINK COPIED', message: 'Battle link copied to clipboard!' });
                        }}>
                            <FaLink />
                        </button>
                        <div className="provably-safe-v4">
                            <FaLock style={{fontSize: '12px', opacity: 0.3}} />
                            <span>PROVABLY SAFE</span>
                        </div>

                    </div>
                </div>
            </div>

            {/* ROUND SUMMARY */}
            <div className="opening-strip-v4">
                <div className="strip-info-v4">
                    <div className="lbl">BATTLE ROUNDS</div>
                    <div className="val">
                        {Math.min(round + 1, battle.cases.length)} <span>OF {battle.cases.length}</span>
                        <div className="strip-modes-v4">
                            {battle.prefs?.map(p => {
                                const meta = PREF_BADGE_META[p];
                                if (!meta) return null;
                                return (
                                    <div key={p} className="strip-mode-icon" style={{ color: meta.color }}>
                                        {meta.icon}
                                        <div className="mode-tooltip">{meta.title}</div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </div>
                <div className="strip-cases-list">
                    {(() => {
                        const maxShow = 4;
                        let startIdx = 0;
                        if (battle.cases.length > maxShow) {
                            if (round >= maxShow - 1) {
                                startIdx = round - maxShow + 2;
                            }
                            startIdx = Math.max(0, Math.min(startIdx, battle.cases.length - maxShow));
                        }
                        const visibleCases = (battle.cases || []).slice(startIdx, startIdx + maxShow);
                        const remaining = battle.cases.length - (startIdx + maxShow);

                        return (
                            <>
                                {visibleCases.map((c, idx) => {
                                    const actualIdx = startIdx + idx;
                                    const state = actualIdx < round ? 'done' : actualIdx === round ? 'current' : 'upcoming';
                                    return (
                                        <div 
                                            key={actualIdx} 
                                            className={`s-case-slot ${state}`}
                                            onContextMenu={(e) => {
                                                e.preventDefault();
                                                onInspect(c);
                                            }}
                                            style={{ cursor: 'help' }}
                                            title="Right-click to Inspect"
                                        >
                                            {state === 'current' && (
                                                <div className="s-case-price-tag">
                                                    <img src="/scrap.webp" alt=""/> {(c.price || 0).toFixed(2)}
                                                </div>
                                            )}
                                            <img src={c.img || 'https://api.dicebear.com/7.x/shapes/svg?seed=case'} alt=""/>
                                            <div className="s-case-idx">{actualIdx + 1}</div>
                                        </div>
                                    );
                                })}
                                {remaining > 0 && (
                                    <div className="s-case-slot upcoming more" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '14px', fontWeight: '900', color: 'rgba(255,255,255,0.2)', background: 'rgba(255,255,255,0.02)', border: '1px dashed rgba(255,255,255,0.05)' }}>
                                        +{remaining}
                                    </div>
                                )}
                            </>
                        );
                    })()}
                </div>
                <div className="strip-payout-v4">
                    <div className="lbl">{battle.creatorId === user?.id ? 'YOUR TOTAL COST' : 'JOIN COST'}</div>
                    <div className="val">
                        <img src="/scrap.webp" alt=""/> 
                        {battle.creatorId === user?.id ? 
                            (battle.cost + (battle.cost * (battle.slots.length - 1) * (battle.fundingPercent / 100))).toFixed(2) : 
                            (battle.fundingPercent === 100 ? 'FREE' : (battle.cost * (1 - (battle.fundingPercent / 100))).toFixed(2))}
                    </div>
                </div>
            </div>

            {/* MAIN MATCHUP PANEL + STATUS + ITEMS HISTORY */}
            {(() => {
                const totalPot = (battle.slots || []).reduce((a, p) => a + (p?.totalValue || 0), 0);
                const isCrazyMode = battle.mode === 'crazy' || battle.isCrazy || battle.prefs?.includes('Crazy');
                const getPct = (v) => {
                    if (totalPot <= 0) return 100 / (battle.slots || [1]).length;
                    if (isCrazyMode) {
                        const weights = (battle.slots || []).map(p => 1 / ((p?.totalValue || 0) + 0.01));
                        const totalWeight = weights.reduce((a, b) => a + b, 0);
                        const myWeight = 1 / ((v || 0) + 0.01);
                        return totalWeight > 0 ? (myWeight / totalWeight) * 100 : (100 / battle.slots.length);
                    }
                    return (v / totalPot) * 100;
                };

                return (
                    <>
                        {/* MAIN MATCHUP PANEL */}
                        <div className="mp-panel">
                            
                            {showJackpotFinal ? (
                                <div className="jackpot-overlay-v4">
                                    <div className="jo-announcement">ROLLING JACKPOT WINNER...</div>
                                    <JackpotWinnerSpinner 
                                        slots={battle.slots} 
                                        winnerId={battle.winners?.[0] || (jackpotWinnerColor ? battle.slots.find((_, i) => COL_PALETTE[i % COL_PALETTE.length].c === jackpotWinnerColor)?.id : null)} 
                                        onDone={() => window._finalizeJackpot?.()} 
                                        isCrazy={isCrazyMode}
                                    />
                                    <div className="jo-subtext">DETERMINING THE WINNER...</div>
                                </div>
                                ) : (showTieBreaker && !battle.isJackpot) ? (
                                <TieBreakerSpin type={tieBreakerData?.type} winnerIds={tieBreakerData?.winnerIds} slots={battle.slots} />
                            ) : (
                                <div className={`mp-matchup ${(battle.slots || []).length > 4 ? 'rows' : 'cols'}`}>
                                { (battle.slots || []).length > 4 ? (
                                    /* --- 3v3 / 4v4 ROWS LAYOUT --- */
                                    <div className="mp-rows-container">
                                        <FaChevronLeft className="mp-arrow left"/>
                                        <div className="mp-rows-grid">
                                            {Array.from({ length: (battle.slots || []).length / 2 }).map((_, rowIndex) => {
                                                const lPlayer = (battle.slots || [])[rowIndex];
                                                const rPlayer = (battle.slots || [])[rowIndex + (battle.slots || []).length / 2];
                                                const lPct = getPct(lPlayer?.totalValue || 0);
                                                const rPct = getPct(rPlayer?.totalValue || 0);
                                                return (
                                                    <div className="mp-row-split" key={rowIndex}>
                                                        <div className="mp-row-side left" style={{ '--col-c': COL_PALETTE[rowIndex % COL_PALETTE.length].c }}>
                                                            <div className="mpr-header" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '10px' }}>
                                                                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                                                    <img className="mp-avatar" src={lPlayer?.avatar || 'https://api.dicebear.com/7.x/pixel-art/svg?seed=placeholder'} alt="" style={{ width: '32px', height: '32px', borderRadius: '6px' }}/>
                                                                </div>
                                                                {battle.prefs?.includes('Jackpot') && (
                                                                    <div style={{ border: `1px solid ${COL_PALETTE[rowIndex % COL_PALETTE.length].c}`, color: COL_PALETTE[rowIndex % COL_PALETTE.length].c, background: `${COL_PALETTE[rowIndex % COL_PALETTE.length].c}22`, padding: '4px 10px', borderRadius: '12px', fontSize: '11px', fontWeight: '900', fontFamily: 'var(--font-mono), monospace' }}>
                                                                        {(lPct || 0).toFixed(2)}%
                                                                    </div>
                                                                )}
                                                            </div>
                                                            <div className="mpr-spinner" key={round}>
                                                                {isSpinning && currentResults[rowIndex] && round < battle.cases.length && (
                                                                    <BattleSpinner targetItem={currentResults[rowIndex]} items={battle.cases[round].items} onComplete={rowIndex === 0 ? onSpinnerDone : () => {}} isFast={battle.prefs?.includes('Fast Mode')} />
                                                                )}
                                                            </div>
                                                        </div>
                                                        <div className="mpr-x-div" style={rowIndex !== Math.floor((battle.slots || []).length / 4) ? { opacity: 0, pointerEvents: 'none' } : {}}>
                                                            <GiCrossedSwords style={{transform: 'rotate(45deg)'}}/>
                                                        </div>
                                                        <div className="mp-row-side right" style={{ '--col-c': COL_PALETTE[(rowIndex + (battle.slots || []).length / 2) % COL_PALETTE.length].c }}>
                                                            <div className="mpr-header" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '10px' }}>
                                                                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                                                    <img className="mp-avatar" src={rPlayer?.avatar || 'https://api.dicebear.com/7.x/pixel-art/svg?seed=placeholder'} alt="" style={{ width: '32px', height: '32px', borderRadius: '6px' }}/>
                                                                </div>
                                                                {battle.prefs?.includes('Jackpot') && (
                                                                    <div style={{ border: `1px solid ${COL_PALETTE[(rowIndex + battle.slots.length / 2) % COL_PALETTE.length].c}`, color: COL_PALETTE[(rowIndex + battle.slots.length / 2) % COL_PALETTE.length].c, background: `${COL_PALETTE[(rowIndex + battle.slots.length / 2) % COL_PALETTE.length].c}22`, padding: '4px 10px', borderRadius: '12px', fontSize: '11px', fontWeight: '900', fontFamily: 'var(--font-mono), monospace' }}>
                                                                        {(rPct || 0).toFixed(2)}%
                                                                    </div>
                                                                )}
                                                            </div>
                                                            <div className="mpr-spinner" key={round}>
                                                                {isSpinning && currentResults[rowIndex + (battle.slots || []).length / 2] && round < battle.cases.length && (
                                                                    <BattleSpinner targetItem={currentResults[rowIndex + (battle.slots || []).length / 2]} items={battle.cases[round].items} onComplete={() => {}} isFast={battle.prefs?.includes('Fast Mode')} />
                                                                )}
                                                            </div>
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                        <FaChevronRight className="mp-arrow right"/>
                                    </div>
                                ) : (
                                    /* --- 1v1 / 2v2 COLUMNS LAYOUT --- */
                                    <div className="mp-cols-container">
                                        <FaChevronLeft className="mp-arrow left"/>
                                        <div className="mp-cols-inner">
                                            {(battle.slots || []).map((p, i) => {
                                                const col = COL_PALETTE[i % COL_PALETTE.length];
                                                const pct = getPct(p?.totalValue || 0);
                                                const isTeamMode = battle.mode === 'team';
                                                const teamHalf = Math.ceil((battle.slots || []).length / 2);
                                                const showXAfter = i < (battle.slots || []).length - 1 && (isTeamMode ? (i === teamHalf - 1) : true);
                                                return (
                                                    <React.Fragment key={p?.id || i}>
                                                        <div className="mp-col" style={{ '--col-c': col.c, '--col-g': col.g }}>
                                                            <div className="mp-top" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '10px' }}>
                                                                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                                                    <img className="mp-avatar" src={p?.avatar || 'https://api.dicebear.com/7.x/pixel-art/svg?seed=placeholder'} alt="" style={{ width: '32px', height: '32px', borderRadius: '6px' }}/>
                                                                </div>
                                                                {battle.prefs?.includes('Jackpot') && (
                                                                    <div style={{ border: `1px solid ${col.c}`, color: col.c, background: `${col.c}22`, padding: '4px 10px', borderRadius: '12px', fontSize: '11px', fontWeight: '900', fontFamily: 'var(--font-mono), monospace' }}>
                                                                        {pct.toFixed(2)}%
                                                                    </div>
                                                                )}
                                                            </div>
                                                            <div className="mp-flow">
                                                                <div className="mp-spinner-wrap horizontal" key={round}>
                                                                    {isSpinning && currentResults[i] && round < battle.cases.length && (
                                                                        <BattleSpinner targetItem={currentResults[i]} items={battle.cases[round].items} onComplete={i === 0 ? onSpinnerDone : () => {}} isFast={battle.prefs?.includes('Fast Mode')} />
                                                                    )}
                                                                </div>
                                                            </div>
                                                        </div>
                                                        {i < (battle.slots || []).length - 1 && (
                                                            <div className="mp-x-div" style={{ 
                                                                background: '#12141c', 
                                                                border: '1px solid #30363d', 
                                                                borderRadius: '8px', 
                                                                width: '36px', 
                                                                height: '36px', 
                                                                transform: 'rotate(45deg)', 
                                                                display: 'flex', 
                                                                alignItems: 'center', 
                                                                justifyContent: 'center', 
                                                                zIndex: 2, 
                                                                margin: '0 -18px',
                                                                boxShadow: '0 0 10px rgba(0,0,0,0.5)'
                                                            }}>
                                                                <div style={{ transform: 'rotate(-45deg)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                                    {isTeamMode ? (
                                                                        (i === teamHalf - 1) ? 
                                                                            <GiCrossedSwords style={{color: '#9ca3af', fontSize: '16px'}}/> : 
                                                                            <FaHandshake style={{color: '#22c55e', fontSize: '16px'}}/>
                                                                    ) : (
                                                                        <GiCrossedSwords style={{color: '#9ca3af', fontSize: '16px'}}/>
                                                                    )}
                                                                </div>
                                                            </div>
                                                        )}
                                                    </React.Fragment>
                                                );
                                            })}
                                        </div>
                                        <FaChevronRight className="mp-arrow right"/>
                                    </div>
                                )}
                            </div>
                        )}

                            {battle.isJackpot && totalPot > 0 && (
                                <div className="mp-pot" style={(battle.slots || []).length > 4 ? {position: 'relative', margin: '20px auto', bottom: 'auto', left: 'auto', transform: 'none'} : {}}>
                                    <FaTrophy/> JACKPOT
                                    <img src="/scrap.webp" alt=""/> {(totalPot || 0).toFixed(2)}
                                </div>
                            )}
                        </div>

                        {/* PLAYER STATUS CARDS */}
                        <div className="ps-row-scroll-wrap">
                            <div className={`ps-row ${battle.mode === 'team' ? 'team-match' : ''}`}>
                                {battle.mode === 'team' ? (
                                    <>
                                        <div className="ps-team-side left">
                                            {(battle.slots || []).slice(0, Math.ceil((battle.slots || []).length/2)).map((p, i) => (
                                                <div className="ps-card" key={p?.id || i} style={{ '--card-color': COL_PALETTE[i % COL_PALETTE.length].c }}>
                                                    <div className="ps-head" style={p ? { display: 'flex', flexDirection: 'column', gap: '12px', alignItems: 'stretch' } : {}}>
                                                        {p ? (
                                                            <>
                                                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                                                                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                                                        <div style={{ position: 'relative' }}>
                                                                            <img src={p.avatar} alt="" style={{ width: '32px', height: '32px', borderRadius: '6px', border: `1px solid ${COL_PALETTE[i % COL_PALETTE.length].c}` }}/>
                                                                        </div>
                                                                        <div style={{ fontSize: '14px', fontWeight: 'bold' }}>{p.username}</div>
                                                                    </div>
                                                                    {p.totalValue > 0 ? (
                                                                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', background: 'rgba(255,255,255,0.05)', padding: '6px 12px', borderRadius: '8px', fontSize: '13px', fontWeight: 'bold', color: '#fff', border: '1px solid rgba(255,255,255,0.05)' }}>
                                                                            <img src="/scrap.webp" alt="" style={{ width: '14px', height: '14px', objectFit: 'contain' }}/>
                                                                            {(p.totalValue || 0).toFixed(2)}
                                                                        </div>
                                                                    ) : (
                                                                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', background: 'rgba(255,255,255,0.05)', padding: '6px 12px', borderRadius: '8px', fontSize: '13px', fontWeight: 'bold', color: 'rgba(255,255,255,0.2)', border: '1px solid rgba(255,255,255,0.05)' }}>
                                                                            <img src="/scrap.webp" alt="" style={{ width: '14px', height: '14px', objectFit: 'contain', opacity: 0.5 }}/>
                                                                            0.00
                                                                        </div>
                                                                    )}
                                                                </div>
                                                                {battle.isJackpot && (
                                                                    <div className="ps-bar-wrap" style={{ width: '100%' }}>
                                                                        <div className="ps-bar-track" style={{ height: '20px', borderRadius: '6px', background: 'rgba(0,0,0,0.5)', border: '1px solid rgba(255,255,255,0.05)', flex: 1, position: 'relative', overflow: 'hidden' }}>
                                                                            <div className="ps-bar-fill" style={{ width: `${getPct(p?.totalValue || 0)}%`, background: COL_PALETTE[i % COL_PALETTE.length].c, height: '100%', borderRadius: '4px', transition: 'width 0.3s', boxShadow: `0 0 10px ${COL_PALETTE[i % COL_PALETTE.length].c}88` }}></div>
                                                                            <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '12px', fontWeight: '900', color: '#fff', textShadow: '0 1px 3px rgba(0,0,0,0.8)', zIndex: 2 }}>
                                                                                {getPct(p?.totalValue || 0).toFixed(2)}%
                                                                            </div>
                                                                        </div>
                                                                    </div>
                                                                )}
                                                            </>
                                                        ) : (
                                                            <>
                                                                <img src={'https://api.dicebear.com/7.x/pixel-art/svg?seed=placeholder'} alt=""/>
                                                                <div className="ps-name">
                                                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                                                                        {(!battle.slots.some(s => s?.id === user?.id)) && (
                                                                            <button className="ps-join-btn" onClick={() => handleJoinBattle(i)}>
                                                                                <FaPlus/> {battle.fundingPercent === 100 ? 'JOIN FREE' : 'JOIN SLOT'}
                                                                            </button>
                                                                        )}
                                                                        <button className="ps-call-bot-btn" onClick={(e) => { e.stopPropagation(); handleAddBot(i); }}>
                                                                            <FaRobot/> CALL BOT
                                                                        </button>
                                                                    </div>
                                                                </div>
                                                            </>
                                                        )}
                                                    </div>
                                                </div>
                                            ))}

                                        </div>
                                        <div className="ps-x-sep"><FaTimes/></div>
                                        <div className="ps-team-side right">
                                            {(battle.slots || []).slice(Math.ceil((battle.slots || []).length/2)).map((p, i) => {
                                                const pIdx = i + Math.ceil((battle.slots || []).length/2);
                                                return (
                                                    <div className="ps-card" key={p?.id || pIdx} style={{ '--card-color': COL_PALETTE[pIdx % COL_PALETTE.length].c }}>
                                                        <div className="ps-head" style={p ? { display: 'flex', flexDirection: 'column', gap: '12px', alignItems: 'stretch' } : {}}>
                                                            {p ? (
                                                                <>
                                                                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                                                                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                                                            <div style={{ position: 'relative' }}>
                                                                                <img src={p.avatar} alt="" style={{ width: '32px', height: '32px', borderRadius: '6px', border: `1px solid ${COL_PALETTE[pIdx % COL_PALETTE.length].c}` }}/>
                                                                            </div>
                                                                            <div style={{ fontSize: '14px', fontWeight: 'bold' }}>{p.username}</div>
                                                                        </div>
                                                                        {p.totalValue > 0 ? (
                                                                            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', background: 'rgba(255,255,255,0.05)', padding: '6px 12px', borderRadius: '8px', fontSize: '13px', fontWeight: 'bold', color: '#fff', border: '1px solid rgba(255,255,255,0.05)' }}>
                                                                                <img src="/scrap.webp" alt="" style={{ width: '14px', height: '14px', objectFit: 'contain' }}/>
                                                                                {(p.totalValue || 0).toFixed(2)}
                                                                            </div>
                                                                        ) : (
                                                                            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', background: 'rgba(255,255,255,0.05)', padding: '6px 12px', borderRadius: '8px', fontSize: '13px', fontWeight: 'bold', color: 'rgba(255,255,255,0.2)', border: '1px solid rgba(255,255,255,0.05)' }}>
                                                                                <img src="/scrap.webp" alt="" style={{ width: '14px', height: '14px', objectFit: 'contain', opacity: 0.5 }}/>
                                                                                0.00
                                                                            </div>
                                                                        )}
                                                                    </div>
                                                                    {battle.isJackpot && (
                                                                        <div className="ps-bar-wrap" style={{ width: '100%' }}>
                                                                            <div className="ps-bar-track" style={{ height: '20px', borderRadius: '6px', background: 'rgba(0,0,0,0.5)', border: '1px solid rgba(255,255,255,0.05)', flex: 1, position: 'relative', overflow: 'hidden' }}>
                                                                                <div className="ps-bar-fill" style={{ width: `${getPct(p?.totalValue || 0)}%`, background: COL_PALETTE[pIdx % COL_PALETTE.length].c, height: '100%', borderRadius: '4px', transition: 'width 0.3s', boxShadow: `0 0 10px ${COL_PALETTE[pIdx % COL_PALETTE.length].c}88` }}></div>
                                                                                <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '12px', fontWeight: '900', color: '#fff', textShadow: '0 1px 3px rgba(0,0,0,0.8)', zIndex: 2 }}>
                                                                                    {getPct(p?.totalValue || 0).toFixed(2)}%
                                                                                </div>
                                                                            </div>
                                                                        </div>
                                                                    )}
                                                                </>
                                                            ) : (
                                                                <>
                                                                    <img src={'https://api.dicebear.com/7.x/pixel-art/svg?seed=placeholder'} alt=""/>
                                                                    <div className="ps-name">
                                                                        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                                                                            {(!battle.slots.some(s => s?.id === user?.id)) && (
                                                                                <button className="ps-join-btn" onClick={() => handleJoinBattle(pIdx)}>
                                                                                    <FaPlus/> {battle.fundingPercent === 100 ? 'JOIN FREE' : 'JOIN SLOT'}
                                                                                </button>
                                                                            )}
                                                                            <button className="ps-call-bot-btn" onClick={(e) => { e.stopPropagation(); handleAddBot(pIdx); }}>
                                                                                <FaRobot/> CALL BOT
                                                                            </button>
                                                                        </div>
                                                                    </div>
                                                                </>
                                                            )}
                                                        </div>
                                                    </div>
                                                );
                                            })}

                                        </div>
                                    </>
                                ) : (
                                    battle.slots.map((p, i) => {
                                        const col = COL_PALETTE[i % COL_PALETTE.length];
                                        const pct = getPct(p?.totalValue || 0);
                                        return (
                                            <div className="ps-card" key={p?.id || i} style={{ '--card-color': col.c }}>
                                                <div className="ps-head" style={p ? { display: 'flex', flexDirection: 'column', gap: '12px', alignItems: 'stretch' } : {}}>
                                                    {p ? (
                                                        <>
                                                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                                                                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                                                    <div style={{ position: 'relative' }}>
                                                                        <img src={p.avatar} alt="" style={{ width: '32px', height: '32px', borderRadius: '6px', border: `1px solid ${col.c}` }}/>
                                                                    </div>
                                                                    <div style={{ fontSize: '14px', fontWeight: 'bold' }}>{p.username}</div>
                                                                </div>
                                                                {p.totalValue > 0 ? (
                                                                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px', background: 'rgba(255,255,255,0.05)', padding: '6px 12px', borderRadius: '8px', fontSize: '13px', fontWeight: 'bold', color: '#fff', border: '1px solid rgba(255,255,255,0.05)' }}>
                                                                        <img src="/scrap.webp" alt="" style={{ width: '14px', height: '14px', objectFit: 'contain' }}/>
                                                                        {(p.totalValue || 0).toFixed(2)}
                                                                    </div>
                                                                ) : (
                                                                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px', background: 'rgba(255,255,255,0.05)', padding: '6px 12px', borderRadius: '8px', fontSize: '13px', fontWeight: 'bold', color: 'rgba(255,255,255,0.2)', border: '1px solid rgba(255,255,255,0.05)' }}>
                                                                        <img src="/scrap.webp" alt="" style={{ width: '14px', height: '14px', objectFit: 'contain', opacity: 0.5 }}/>
                                                                        0.00
                                                                    </div>
                                                                )}
                                                            </div>
                                                            {battle.isJackpot && (
                                                                <div className="ps-bar-wrap" style={{ width: '100%' }}>
                                                                    <div className="ps-bar-track" style={{ height: '20px', borderRadius: '6px', background: 'rgba(0,0,0,0.5)', border: '1px solid rgba(255,255,255,0.05)', flex: 1, position: 'relative', overflow: 'hidden' }}>
                                                                        <div className="ps-bar-fill" style={{ width: `${pct}%`, background: col.c, height: '100%', borderRadius: '4px', transition: 'width 0.3s', boxShadow: `0 0 10px ${col.c}88` }}></div>
                                                                        <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '12px', fontWeight: '900', color: '#fff', textShadow: '0 1px 3px rgba(0,0,0,0.8)', zIndex: 2 }}>
                                                                            {pct.toFixed(2)}%
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            )}
                                                        </>
                                                    ) : (
                                                        <>
                                                            <img src={'https://api.dicebear.com/7.x/pixel-art/svg?seed=placeholder'} alt=""/>
                                                            <div className="ps-name">
                                                                <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                                                                    {(!battle.slots.some(s => s?.id === user?.id)) && (
                                                                        <button className="ps-join-btn" onClick={() => handleJoinBattle(i)}>
                                                                            <FaPlus/> {battle.fundingPercent === 100 ? 'JOIN FREE' : 'JOIN SLOT'}
                                                                        </button>
                                                                    )}
                                                                    <button className="ps-call-bot-btn" onClick={(e) => { e.stopPropagation(); handleAddBot(i); }}>
                                                                        <FaRobot/> CALL BOT
                                                                    </button>
                                                                </div>
                                                            </div>
                                                        </>
                                                    )}
                                                </div>
                                            </div>
                                        )
                                    })
                                )}
                            </div>
                        </div>

                        {/* ITEMS HISTORY GRID */}
                        <div className="ih-wrap">
                            <div className="ih-title"><FaHistory/> ROUND HISTORY</div>
                            <div className="ih-grid" style={{ 
                                gridTemplateColumns: `60px repeat(${(battle.slots || []).length / (battle.mode === 'team' ? 2 : 1)}, 1fr)${battle.mode === 'team' ? ' 50px repeat(' + ((battle.slots || []).length / 2) + ', 1fr)' : ''}`,
                                gap: '15px'
                            }}>
                                {/* HEADER ROW */}
                                <div className="ih-header-round">ROUND</div>
                                {battle.mode === 'team' ? (
                                    <>
                                        {(battle.slots || []).slice(0, (battle.slots || []).length/2).map((p, i) => (
                                            <div key={`h-${i}`} className="ih-header-player">
                                                <img src={p?.avatar || 'https://api.dicebear.com/7.x/pixel-art/svg?seed=placeholder'} alt=""/>
                                                <span>{p?.username || 'Waiting...'}</span>
                                            </div>
                                        ))}
                                        <div className="ih-header-sep"><FaTimes/></div>
                                        {(battle.slots || []).slice((battle.slots || []).length/2).map((p, i) => (
                                            <div key={`h-r-${i}`} className="ih-header-player">
                                                <img src={p?.avatar || 'https://api.dicebear.com/7.x/pixel-art/svg?seed=placeholder'} alt=""/>
                                                <span>{p?.username || 'Waiting...'}</span>
                                            </div>
                                        ))}
                                    </>
                                ) : (
                                    battle.slots.map((p, i) => (
                                        <div key={`h-${i}`} className="ih-header-player">
                                            <img src={p?.avatar || 'https://api.dicebear.com/7.x/pixel-art/svg?seed=placeholder'} alt=""/>
                                            <span>{p?.username || 'Waiting...'}</span>
                                        </div>
                                    ))
                                )}

                                {/* HISTORY ROWS */}
                                {Array.from({ length: battle.cases.length }).flatMap((_, rIdx) => {
                                    const rows = [];
                                    
                                    // 1. Round Number
                                    rows.push(<div key={`r-${rIdx}`} className="ih-round-num">#{rIdx + 1}</div>);

                                    const renderCell = (p, pIdx) => {
                                        const col = COL_PALETTE[pIdx % COL_PALETTE.length];
                                        const itemData = p?.unboxedItems?.[rIdx];
                                        const item = Array.isArray(itemData) ? itemData[0] : itemData;
                                        const key = `${rIdx}-${p?.id || pIdx}`;
                                        if (!item) {
                                            return (
                                                <div key={key} className="ih-placeholder">
                                                    <div className="ih-dot"></div>
                                                </div>
                                            );
                                        }
                                        const itemColor = item.color || col.c;
                                        return (
                                            <motion.div
                                                key={key}
                                                className="ih-item"
                                                initial={{ opacity: 0, y: -6 }}
                                                animate={{ opacity: 1, y: 0 }}
                                                transition={{ duration: 0.25 }}
                                                style={{ borderLeft: `3px solid ${itemColor}` }}
                                            >
                                                <img src={item.img} alt=""/>
                                                <div className="ih-meta">
                                                    <div className="ih-name" style={{ color: itemColor, display: 'flex', alignItems: 'center', gap: '6px' }}>
                                                        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{item.name}</span>
                                                        {battle.cases[rIdx]?.items?.find(i => i.name === item.name)?.chance && (
                                                            <span style={{ fontSize: '9px', opacity: 0.8, background: 'rgba(0,0,0,0.4)', padding: '2px 4px', borderRadius: '4px', flexShrink: 0 }}>
                                                                {battle.cases[rIdx].items.find(i => i.name === item.name).chance}%
                                                            </span>
                                                        )}
                                                    </div>
                                                    <div className="ih-price">
                                                        <img src="/scrap.webp" alt=""/> {(item.price || 0).toFixed(2)}
                                                    </div>
                                                </div>
                                            </motion.div>
                                        );
                                    };

                                    if (battle.mode === 'team') {
                                        const half = (battle.slots || []).length / 2;
                                        (battle.slots || []).slice(0, half).forEach((p, i) => rows.push(renderCell(p, i)));
                                        rows.push(<div key={`sep-${rIdx}`} className="ih-row-sep"></div>);
                                        (battle.slots || []).slice(half).forEach((p, i) => rows.push(renderCell(p, i + half)));
                                    } else {
                                        (battle.slots || []).forEach((p, i) => rows.push(renderCell(p, i)));
                                    }

                                    return rows;
                                })}
                            </div>
                        </div>

                    </>
                );
            })()}
        </div>
    );
};

/* --- RESULTS VIEW v5 --- */
const BattleResultsView = ({ battleId, battles, onBack, onInspect, onRecreate, onEdit, user }) => {
    const battle = battles.find(b => b.id === battleId);
    if (!battle) return null;

    const winners = (battle.slots || []).filter(s => (battle.winners || []).includes(s?.id));
    const winIds = battle.winners || [];
    const totalPrize = (battle.slots || []).reduce((a, p) => a + (p?.totalValue || 0), 0); 
    const winAmountPerPlayer = winIds.length > 0 ? totalPrize / winIds.length : 0;
    
    const isPlayerWinner = (pid) => winIds.includes(pid);
    const isUserWinner = winners.some(w => w.id === user?.id || w.username === user?.username || w.username === 'You');

    const numPlayers = battle.slots.length;
    const isTeam = battle.mode === 'team';
    const half = Math.ceil(numPlayers / 2);
    const totalPot = (battle.slots || []).reduce((a, p) => a + (p?.totalValue || 0), 0);
    const matchup = isTeam ? `${numPlayers/2}v${numPlayers/2}` : Array(numPlayers || 1).fill('1').join('v');
    const modeLabel = `${matchup} ${battle.isJackpot ? 'JACKPOT' : (battle.mode || 'NORMAL').toUpperCase()} MODE`;
    const RES_PALETTE = [
        { c: '#ffb703' }, { c: '#3b82f6' }, { c: '#22c55e' },
        { c: '#ef4444' }, { c: '#3b82f6' }, { c: '#ffb703' },
    ];

    return (
        <div className="rv5-main">

            {/* ── TOP NAV ── */}
            <div className="rv5-nav">
                <div className="rv5-nav-left">
                    <div className="b-back" onClick={onBack}><FaChevronLeft/> CASE BATTLES</div>
                    <div className="rv5-cost-pill"><img src="/scrap.webp" alt=""/>{battle.cost.toFixed(2)}</div>
                </div>
                <div className="rv5-nav-center">
                    <div className="rv5-nav-avatars">
                        {(battle.slots || []).map((s, i) => (
                            <img key={i} src={s?.avatar || 'https://api.dicebear.com/7.x/pixel-art/svg?seed=placeholder'} alt="" className="rv5-nav-av"/>
                        ))}
                    </div>
                    <div className="rv5-mode-lbl">{modeLabel}</div>
                </div>
                <div className="rv5-nav-right">
                    <button className="p-btn-square" onClick={() => {
                        navigator.clipboard.writeText(`${window.location.origin}${window.location.pathname}#/battle/${battle.id}`);
                        alert('Battle link copied!');
                    }}><FaLink/></button>
                    <div className="p-btn-label"><FaLock className="ico"/> PROVABLY FAIR</div>

                </div>
            </div>

            {/* ── ROUNDS STRIP ── */}
            <div className="rv5-strip">
                <div className="rv5-strip-info">
                    <div className="lbl">BATTLE ROUNDS</div>
                    <div className="val">
                        {battle.cases.length} <span>OF {battle.cases.length}</span>
                        <div className="strip-modes-v4">
                            {battle.prefs?.map(p => {
                                const meta = PREF_BADGE_META[p];
                                if (!meta) return null;
                                return <div key={p} className="strip-mode-icon" style={{ color: meta.color }}>{meta.icon}<div className="mode-tooltip">{meta.title}</div></div>;
                            })}
                        </div>
                    </div>
                </div>
                <div className="strip-cases-list">
                    {(battle.cases || []).slice(0, 4).map((c, i) => (
                        <div key={i} className="s-case-slot done" title="Right-click to Inspect"
                            onContextMenu={e => { e.preventDefault(); onInspect(c); }}>
                            <img src={c.img || 'https://api.dicebear.com/7.x/shapes/svg?seed=case'} alt=""/>
                            <div className="s-case-idx">{i + 1}</div>
                        </div>
                    ))}
                    {battle.cases.length > 4 && (
                        <div className="s-case-slot done more" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '14px', fontWeight: '900', color: 'rgba(255,255,255,0.2)', background: 'rgba(255,255,255,0.02)', border: '1px dashed rgba(255,255,255,0.05)' }}>
                            +{battle.cases.length - 4}
                        </div>
                    )}
                </div>

            </div>

            {/* ── VICTORY BOX ── */}
            <div className="rv5-victory-box">
                <div className="rv5-victory-rays"/>
                <div className="rv5-winner-avatars">
                    {winners.map(w => (
                        <div key={w.id} className="rv5-win-av">
                            <FaCrown className="rv5-crown"/>
                            <img src={w.avatar || 'https://api.dicebear.com/7.x/pixel-art/svg?seed=placeholder'} alt=""/>
                            {w.isBot && <div className="rv5-bot-tag">BOT</div>}
                        </div>
                    ))}
                </div>
                <h2 className="rv5-win-title">
                    {isTeam ? `TEAM ${winners.some(w => (battle.slots || []).findIndex(s => s?.id === w.id) < half) ? '1' : '2'} WON` : (winners.length > 1 ? 'DRAW! SPLIT WIN' : (winners[0]?.username || 'UNKNOWN').toUpperCase() + ' WON')}
                </h2>
                <div className="rv5-win-payout">
                    <img src="/scrap.webp" alt=""/>
                    <span className="rv5-payout-num">
                        {(totalPrize || 0).toFixed(2)}
                    </span>


                </div>
                
                {winners.length > 1 && (
                    <div className="rv5-payout-share-lbl">
                        <FaUsers style={{marginRight: '6px', opacity: 0.5}} />
                        SPLIT WIN: <img src="/scrap.webp" style={{width:11, margin: '0 4px'}} alt=""/> 
                        <b>{(winAmountPerPlayer || 0).toFixed(2)}</b> EACH
                    </div>
                )}

                <button className="rv5-recreate-btn" onClick={onRecreate}>
                    <FaSyncAlt/> RECREATE BATTLE <img src="/scrap.webp" alt="" style={{width:'13px',marginLeft:'6px'}}/> {(battle.cost || 0).toFixed(2)}
                </button>
                <div className="rv5-edit-link" onClick={onEdit}><FaSearch style={{fontSize:'10px'}}/> EDIT BATTLE</div>
            </div>

            {/* ── PLAYER COLUMNS LAYOUT ── */}
            <div className="rv5-results-container">
                <div className="rv5-results-row">
                    {(battle.slots || []).map((s, i) => {
                        const isWinner = battle.winners?.includes(s?.id);
                        const col = RES_PALETTE[i % RES_PALETTE.length].c;
                        const isCrazyMode = battle.mode === 'crazy' || battle.isCrazy || battle.prefs?.includes('Crazy');
                        const getP = (v) => {
                            if (totalPot <= 0) return 100 / (battle.slots || [1]).length;
                            if (isCrazyMode) {
                                const weights = (battle.slots || []).map(p => 1 / ((p?.totalValue || 0) + 0.01));
                                const totalWeight = weights.reduce((acc, w) => acc + w, 0);
                                const myWeight = 1 / ((v || 0) + 0.01);
                                return totalWeight > 0 ? (myWeight / totalWeight) * 100 : (100 / battle.slots.length);
                            }
                            return (v / totalPot) * 100;
                        };
                        const pct = getP(s?.totalValue || 0);
                        const showTeamX = isTeam && i === half - 1;

                        return (
                            <React.Fragment key={s?.id || i}>
                                <div className="rv5-player-column">
                                    {/* Player Card */}
                                    <div className={`rv5-player-card ${isWinner ? 'winner' : ''}`} style={{ '--pc': col }}>
                                        {/* Win Payout Badge - ONLY FOR WINNERS */}
                                        {isWinner && (
                                            <div className="rv5-player-profit-badge">
                                                <img src="/scrap.webp" alt=""/>
                                                {(winAmountPerPlayer || 0).toFixed(2)}
                                            </div>
                                        )}

                                        <img src={s?.avatar || `https://api.dicebear.com/7.x/pixel-art/svg?seed=${s?.username || i}`} alt="" className="rv5-pc-av"/>
                                        <div className="rv5-pc-name">{s?.username || '—'}</div>
                                        <div className={`rv5-pc-val`}>
                                            <img src="/scrap.webp" alt=""/> 
                                            <b>{(s?.totalValue || 0).toFixed(2)}</b>
                                        </div>
                                        {battle.isJackpot && (
                                            <>
                                                <div className="rv5-pc-bar-track">
                                                    <motion.div className="rv5-pc-bar-fill"
                                                        initial={{ width: 0 }}
                                                        animate={{ width: `${pct}%` }}
                                                        transition={{ duration: 1, delay: 0.5 }}
                                                        style={{ background: col }}
                                                    />
                                                </div>
                                                <div className="rv5-pc-pct" style={{ color: col }}>{(pct || 0).toFixed(2)}%</div>
                                            </>
                                        )}
                                    </div>

                                    {/* Items List */}
                                    <div className="rv5-player-items">
                                        {(s?.unboxedItems || []).map((itemData, rIdx) => {
                                            const item = Array.isArray(itemData) ? itemData[0] : itemData;
                                            const key = `item-${i}-${rIdx}`;
                                            if (!item) return <div key={key} className="rv5-item-empty"><div className="ih-dot"/></div>;
                                            
                                            const caseObj = battle.cases[rIdx];
                                            const casePrice = caseObj?.price || 0;
                                            const caseName = caseObj?.name || 'Unknown Case';

                                            const ic = item.color || col;
                                            return (
                                                <motion.div key={key} className="rv5-item-card"
                                                    initial={{ opacity: 0, y: -4 }}
                                                    animate={{ opacity: 1, y: 0 }}
                                                    transition={{ duration: 0.2, delay: (rIdx * numPlayers + i) * 0.015 }}
                                                    style={{ borderBottom: `2px solid ${ic}` }}
                                                >
                                                    {/* Default View: Unboxed Item */}
                                                    <div className="rv5-item-inner rv5-item-default">
                                                        <img src={item.img} alt=""/>
                                                        <div className="rv5-item-meta">
                                                            <div className="rv5-item-name" style={{ color: ic, display: 'flex', alignItems: 'center', gap: '4px' }}>
                                                                <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{item.name}</span>
                                                                {caseObj?.items?.find(i => i.name === item.name)?.chance && (
                                                                    <span style={{ fontSize: '9px', opacity: 0.8, background: 'rgba(0,0,0,0.4)', padding: '1px 4px', borderRadius: '4px', flexShrink: 0 }}>
                                                                        {caseObj.items.find(i => i.name === item.name).chance}%
                                                                    </span>
                                                                )}
                                                            </div>
                                                            <div className="rv5-item-price"><img src="/scrap.webp" alt=""/> {(item.price || 0).toFixed(2)}</div>
                                                        </div>
                                                    </div>

                                                    {/* Hover View: Source Case */}
                                                    <div className="rv5-item-inner rv5-item-hover">
                                                        <img src={caseObj?.img || '/case.gif'} alt=""/>
                                                        <div className="rv5-item-meta">
                                                            <div className="rv5-item-name" style={{ color: '#fff', opacity: 0.8 }}>{caseName}</div>
                                                            <div className="rv5-item-price"><img src="/scrap.webp" alt=""/> {(casePrice || 0).toFixed(2)}</div>
                                                        </div>
                                                    </div>
                                                </motion.div>
                                            );
                                        })}
                                    </div>
                                </div>
                                {showTeamX && <div className="rv5-team-separator"><FaTimes/></div>}
                            </React.Fragment>
                        );
                    })}
                </div>
            </div>

        </div>
    );
};

export default CaseBattles;

